import { useState, useEffect, useCallback, useRef } from "react";
import html2canvas from "html2canvas";

const FONTS = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,500;0,600;0,700;1,500;1,600&family=Lora:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap";
const ds = { fontFamily: "'Playfair Display', Georgia, serif" };
const bs = { fontFamily: "'Lora', Georgia, serif" };

// ── DESIGN SYSTEM ──
const C = {
  bg:       "#FDF6E9",   // warm parchment
  bgDeep:   "#F5ECD6",   // deeper parchment
  green:    "#2C4A2E",   // deep forest green
  greenMid: "#4a7c59",   // mid green
  greenLight:"#6aab7a",  // light green
  gold:     "#C9A84C",   // warm gold
  goldDeep: "#A8852F",   // deep gold — readable on parchment
  goldLight:"#E8C96A",   // light gold
  goldPale: "#F5E9C8",   // pale gold
  cream:    "#FFFDF5",   // pure cream
  text:     "#1C2E1E",   // deep text
  textMid:  "#4a5a4a",   // mid text
  textSoft: "#8a9a7a",   // soft text
  surface:  "rgba(255,255,255,0.85)",
  border:   "rgba(44,74,46,0.12)",
  shadow:   "0 4px 24px rgba(28,46,30,0.10)",
  shadowLg: "0 8px 40px rgba(28,46,30,0.14)",
};

const GRAD = {
  bg:     `radial-gradient(130% 55% at 50% 0%, rgba(201,168,76,0.09) 0%, rgba(201,168,76,0) 60%), linear-gradient(160deg, ${C.bg} 0%, #EEF5E4 52%, ${C.bgDeep} 100%)`,
  green:  `linear-gradient(160deg, #3A5F3D 0%, ${C.green} 55%, #213823 100%)`,
  gold:   `linear-gradient(160deg, ${C.goldLight} 0%, ${C.gold} 55%, #AE8C38 100%)`,
  dark:   `linear-gradient(135deg, ${C.green}, #1a2e1c)`,
  card:   `linear-gradient(160deg, ${C.cream} 0%, rgba(245,233,200,0.3) 100%)`,
};

// ─────────────────────────────────────────────
// AFFIRMATION LIBRARY
// ─────────────────────────────────────────────
const AFFIRMATIONS = {
  Spiritual: ["I am divinely guided and protected in everything I do.","God's blessings overflow in my life every single day.","I am a vessel of love, light, and divine purpose.","The Universe conspires in my favor at all times.","I walk in faith, knowing that all things work together for my good.","Every prayer I speak is heard and answered in perfect time.","I am worthy of every blessing Heaven has for me.","My spirit is strong, renewed, and full of divine peace.","Miracles are a natural part of my everyday life.","Heaven's abundance flows freely and continuously into my life.","I am deeply loved by the Creator of all things."],
  Wealth: ["Money flows to me easily, freely, and abundantly.","I am a magnet for wealth, prosperity, and financial freedom.","My income increases every single month beyond what I expect.","Wealth is my birthright and I claim it fully today.","Every dollar I spend comes back to me multiplied.","I build generational wealth for myself and my family.","Abundance is my natural state of being.","Financial freedom is already mine — I am stepping into it now.","My business generates more than enough to bless me and others.","Prosperity follows me everywhere I go.","New streams of income open up to me constantly.","I am debt-free, abundant, and financially empowered."],
  Health: ["My body is strong, vibrant, and full of healing energy.","Every cell in my body radiates perfect health.","I honor my body with nourishment, rest, and love.","My mind is clear, my body is strong, my spirit is at peace.","I wake up every morning feeling refreshed and energized.","My body knows exactly how to heal and restore itself.","I treat my body as the sacred temple it truly is.","I am in perfect alignment — mind, body, and soul.","My energy is limitless and my stamina is incredible.","I am becoming healthier, stronger, and more vibrant daily.","Healing is happening in my body right now."],
  Love: ["I am deeply loved, cherished, and adored.","Love surrounds me in every area of my life.","I attract relationships that are healthy, nurturing, and real.","I am worthy of a deep, passionate, and committed love.","My heart is open and ready to give and receive fully.","My relationships are filled with joy, laughter, and growth.","I love myself fully, and that love attracts more love.","I deserve to be chosen, valued, and deeply respected.","My family bonds grow stronger and more beautiful each day."],
  Relationships: ["I am surrounded by people who genuinely love and support me.","My friendships are deep, loyal, and life-giving.","I attract positive, ambitious, and kind people into my life.","I communicate with confidence, clarity, and compassion.","I set healthy boundaries and they are always respected.","I bring out the best in the people around me.","People are drawn to my warmth, energy, and authenticity.","My network is filled with successful, loving, purposeful people.","I am building a legacy of love within my family."],
  Mindset: ["I believe in myself completely and without condition.","My mind is powerful, focused, and unstoppable.","I choose joy, gratitude, and abundance in every moment.","Challenges make me stronger — I rise through every one.","I am disciplined, consistent, and committed to my goals.","My confidence grows stronger with every single step I take.","I am worthy of every dream I have dared to dream.","Success is not a question of if — only when.","I am brave enough to pursue the life I truly desire.","Every day I am becoming a better version of myself.","I am resilient, resourceful, and ready for greatness."],
};

// ─────────────────────────────────────────────
// "I AM" DECLARATION LIBRARY
// ─────────────────────────────────────────────
const IAM_LIBRARY = {
  Spiritual: [
    "I Am a child of God, fully loved and fully chosen.",
    "I Am walking in divine purpose every single day.",
    "I Am guided by a power greater than myself.",
    "I Am protected, covered, and favored by God.",
    "I Am a living testimony of God's grace.",
    "I Am rooted in faith that cannot be shaken.",
    "I Am aligned with the will of God for my life.",
    "I Am a miracle in motion.",
    "I Am filled with the peace that surpasses all understanding.",
    "I Am exactly who God created me to be.",
    "I Am anointed, appointed, and set apart.",
    "I Am walking in divine favor that cannot be stopped.",
    "I Am covered by grace in every step I take.",
    "I Am a vessel of God's love poured out into the world.",
    "I Am called to greatness and I answer that call.",
    "I Am spiritually grounded and unshakeable.",
    "I Am connected to the source of all abundance.",
    "I Am a prayer warrior and my prayers move mountains.",
    "I Am divinely positioned for my next blessing.",
    "I Am a beacon of light in every room I enter.",
  ],
  Wealth: [
    "I Am a millionaire in the making.",
    "I Am financially free and debt-free.",
    "I Am a magnet for money and opportunity.",
    "I Am building generational wealth right now.",
    "I Am a prosperous and abundant person.",
    "I Am worthy of every financial blessing coming my way.",
    "I Am a wise steward of everything God has given me.",
    "I Am creating multiple streams of income.",
    "I Am living in overflow, not lack.",
    "I Am a successful entrepreneur.",
    "I Am rich in every area of my life.",
    "I Am a six-figure earner and growing.",
    "I Am attracting wealth effortlessly and consistently.",
    "I Am a money magnet — it flows to me from every direction.",
    "I Am investing wisely and my wealth is multiplying.",
    "I Am the first of many millionaires in my family.",
    "I Am deserving of luxury, comfort, and financial peace.",
    "I Am free from financial stress forever.",
    "I Am a generous giver because I have more than enough.",
    "I Am leaving a financial legacy my family will build on.",
  ],
  Health: [
    "I Am strong, healthy, and full of energy.",
    "I Am healing every single day.",
    "I Am in the best shape of my life.",
    "I Am at my ideal weight and loving it.",
    "I Am vibrant, radiant, and glowing.",
    "I Am committed to taking care of my body.",
    "I Am free from pain, illness, and disease.",
    "I Am mentally sharp, focused, and clear.",
    "I Am the picture of perfect health.",
    "I Am energized and ready for each new day.",
    "I Am physically strong and emotionally balanced.",
    "I Am nourishing my body with everything it needs.",
    "I Am getting better, stronger, and healthier every day.",
    "I Am sleeping deeply and waking up fully restored.",
    "I Am a living example of what wellness looks like.",
    "I Am full of vitality and my body responds beautifully.",
    "I Am disciplined in my habits and it shows.",
    "I Am grateful for this body and I honor it daily.",
    "I Am free from anxiety and at peace in my mind.",
    "I Am aging gracefully and feeling better than ever.",
  ],
  Love: [
    "I Am deeply and completely loved.",
    "I Am worthy of a great love story.",
    "I Am in a beautiful, thriving relationship.",
    "I Am love — I give it and I receive it freely.",
    "I Am attracting my perfect partner.",
    "I Am cherished, valued, and adored.",
    "I Am surrounded by people who love me unconditionally.",
    "I Am building a family filled with love and joy.",
    "I Am healed from past heartbreak and open to new love.",
    "I Am someone's answered prayer.",
    "I Am magnetic to genuine, deep, lasting love.",
    "I Am in a relationship that brings out the best in me.",
    "I Am chosen every single day by someone who loves me fully.",
    "I Am a loving, nurturing, and present partner.",
    "I Am worthy of being pursued, cherished, and respected.",
    "I Am at peace with love — giving and receiving it freely.",
    "I Am building a home filled with warmth, laughter, and safety.",
    "I Am the love I've always wanted — it starts with me.",
  ],
  Relationships: [
    "I Am a loyal and trustworthy friend.",
    "I Am a magnet for genuine, meaningful connections.",
    "I Am surrounded by people who celebrate my success.",
    "I Am a connector of great people and great ideas.",
    "I Am building a powerful and supportive community.",
    "I Am a loving parent, partner, and friend.",
    "I Am someone people trust and look up to.",
    "I Am creating relationships that last a lifetime.",
    "I Am known for my kindness and my character.",
    "I Am surrounded by ambitious, faith-filled, positive people.",
    "I Am a person of influence and I use it for good.",
    "I Am worthy of deep, authentic, and fulfilling friendships.",
    "I Am a safe place for people who need one.",
    "I Am building a circle that sharpens and elevates me.",
    "I Am intentional about who I pour my energy into.",
    "I Am leaving every person I meet better than I found them.",
    "I Am someone who shows up fully in every relationship.",
  ],
  Mindset: [
    "I Am unstoppable.",
    "I Am confident in who I am and where I am going.",
    "I Am disciplined, focused, and consistent.",
    "I Am becoming better every single day.",
    "I Am capable of achieving anything I set my mind to.",
    "I Am a problem solver and a solution finder.",
    "I Am resilient — I rise every time I fall.",
    "I Am more than enough.",
    "I Am the CEO of my own life.",
    "I Am bold, brave, and unapologetically myself.",
    "I Am creating a life I am proud of.",
    "I Am a winner.",
    "I Am not moved by fear — I am moved by faith.",
    "I Am laser focused on what matters most.",
    "I Am worthy of every dream I have ever dared to dream.",
    "I Am a visionary — I see what others cannot yet see.",
    "I Am in control of my thoughts and my thoughts create my life.",
    "I Am always growing, always learning, always evolving.",
    "I Am patient with my process and trust my timing.",
    "I Am exactly where I need to be, moving exactly where I'm meant to go.",
    "I Am mentally strong and emotionally intelligent.",
    "I Am someone who finishes what they start.",
    "I Am built for this — every challenge, every season.",
  ],
};

const IAM_CATEGORIES = ["Spiritual","Wealth","Health","Love","Relationships","Mindset"];

// ── COMPLETION MESSAGES ──
const COMPLETION_MESSAGES = [
  "Every word you just spoke was a seed planted. God waters what you declare in faith.",
  "You showed up for yourself today. That's what champions do.",
  "The enemy fears a person who knows who they are. You just reminded him.",
  "Your words have power. Heaven heard every single one.",
  "You didn't just read words — you declared your destiny.",
  "Most people never speak their truth out loud. You just did. That's the difference.",
  "Faith without declaration is just a wish. You made it real today.",
  "You are exactly who you just said you are. Walk in it.",
  "Every declaration builds the person you're becoming. Keep going.",
  "God doesn't call the qualified — He qualifies the called. And you just answered.",
  "You spoke it. You believed it. Now watch God move.",
  "Consistency is the secret weapon of every person who made it. You're building it daily.",
  "The life you want is on the other side of what you just declared.",
  "You are not who you used to be. These declarations prove it.",
  "Greatness isn't given — it's declared, believed, and pursued. You just did step one.",
  "Your mouth is a weapon. You just used it wisely.",
  "Someone is praying for the life you're building. Don't stop now.",
  "God's plan for you is bigger than your past. These words are your proof.",
  "You could have skipped this today. You didn't. That's who you are.",
  "Every morning you declare is a morning the devil loses.",
  "You are covered, chosen, and called. Act like it.",
  "The version of you that wins — they did exactly what you just did.",
  "Your future self is thanking you right now.",
  "Small daily declarations build unshakeable people. You're becoming one.",
  "What God has for you is already yours. These words just remind your spirit.",
  "You are not average. You never were. These declarations just confirmed it.",
  "Faith is spelled A-C-T-I-O-N. You just took yours.",
  "The breakthrough you're believing for starts with moments exactly like this one.",
  "You are dangerous to every limitation that tried to define you.",
  "Declare it in the dark and watch God bring it to the light.",
];

// ── TRIAL CONFIG ──
const TRIAL_DAYS = 7;

const CATEGORIES = ["Spiritual","Wealth","Health","Love","Relationships","Mindset"];
const CAT_META = {
  Spiritual:     { color:"#9b7fc8", light:"#f2edf9", emoji:"🙏" },
  Wealth:        { color:"#c8a254", light:"#faf4e2", emoji:"💰" },
  Health:        { color:"#5fa36e", light:"#e6f3ea", emoji:"🌿" },
  Love:          { color:"#c87878", light:"#faeaea", emoji:"❤️" },
  Relationships: { color:"#7a9abf", light:"#e6eff8", emoji:"🤝" },
  Mindset:       { color:"#b08060", light:"#f6ede4", emoji:"🔥" },
};

const IMAGE_LIBRARY = {
  Wealth: [
    { url:"https://picsum.photos/seed/wealth1/600/400", label:"Financial Freedom" },
    { url:"https://picsum.photos/seed/wealth2/600/500", label:"Prosperity" },
    { url:"https://picsum.photos/seed/wealth3/600/450", label:"Success" },
    { url:"https://picsum.photos/seed/wealth4/600/400", label:"Luxury Life" },
    { url:"https://picsum.photos/seed/wealth5/600/500", label:"Growth" },
    { url:"https://picsum.photos/seed/wealth6/600/450", label:"Abundance" },
  ],
  Health: [
    { url:"https://picsum.photos/seed/health1/600/450", label:"Strong Body" },
    { url:"https://picsum.photos/seed/health2/600/400", label:"Nourishment" },
    { url:"https://picsum.photos/seed/health3/600/500", label:"Inner Peace" },
    { url:"https://picsum.photos/seed/health4/600/450", label:"Vitality" },
    { url:"https://picsum.photos/seed/health5/600/400", label:"Fitness" },
  ],
  Love: [
    { url:"https://picsum.photos/seed/love1/600/500", label:"True Love" },
    { url:"https://picsum.photos/seed/love2/600/450", label:"Family" },
    { url:"https://picsum.photos/seed/love3/600/400", label:"Connection" },
    { url:"https://picsum.photos/seed/love4/600/500", label:"Romance" },
  ],
  Spiritual: [
    { url:"https://picsum.photos/seed/spirit1/600/450", label:"Meditation" },
    { url:"https://picsum.photos/seed/spirit2/600/500", label:"Nature & Soul" },
    { url:"https://picsum.photos/seed/spirit3/600/400", label:"Sunrise Faith" },
    { url:"https://picsum.photos/seed/spirit4/600/450", label:"Divine Light" },
  ],
  Relationships: [
    { url:"https://picsum.photos/seed/relate1/600/400", label:"Friendship" },
    { url:"https://picsum.photos/seed/relate2/600/500", label:"Support Circle" },
    { url:"https://picsum.photos/seed/relate3/600/450", label:"Team & Growth" },
  ],
  Mindset: [
    { url:"https://picsum.photos/seed/mind1/600/450", label:"Focus & Purpose" },
    { url:"https://picsum.photos/seed/mind2/600/400", label:"Vision" },
    { url:"https://picsum.photos/seed/mind3/600/500", label:"Limitless" },
    { url:"https://picsum.photos/seed/mind4/600/450", label:"Achievement" },
  ],
};

const DREAM_QUESTIONS = [
  { id:"home",           emoji:"🏡", question:"Where do you live?",                                        subtitle:"Describe your dream home and where in the world it is.",              placeholder:"I live in a beautiful 5-bedroom home in Atlanta with a pool...",  chips:["Luxury home in the suburbs","Penthouse in a major city","Beachfront property","Estate in the countryside","Traveling the world full-time"] },
  { id:"work",           emoji:"💼", question:"What do you do for work?",                                   subtitle:"Are you your own boss? What does your ideal career look like?",        placeholder:"I run my own thriving business on my own schedule...",            chips:["My own business — fully thriving","Multiple streams of passive income","Creative career doing what I love","Real estate investor","Completely financially free"] },
  { id:"finances",       emoji:"💰", question:"What does your financial life look like?",                   subtitle:"Income, freedom, generational wealth — paint the picture.",            placeholder:"I earn multiple six figures and am completely debt-free...",       chips:["Six-figure income monthly","Completely debt-free","Generational wealth built","Investments growing passively","Giving generously to others"] },
  { id:"relationships",  emoji:"👨‍👩‍👧", question:"Who is in your life?",                                        subtitle:"Partner, family, friendships, community — who surrounds you?",       placeholder:"I am married to my soulmate and my children are thriving...",     chips:["Married to my soulmate","Thriving children and family","Deep loyal friendships","Powerful success-minded community","Strong faith community"] },
  { id:"health",         emoji:"💪", question:"What does your health and body feel like?",                  subtitle:"Energy, fitness, how you feel every single morning.",                  placeholder:"I wake up with incredible energy and feel fully alive...",        chips:["Wake up energized every day","At my ideal healthy weight","Strong and physically fit","No chronic pain or illness","Glowing, vibrant, and radiant"] },
  { id:"time",           emoji:"✈️", question:"How do you spend your time?",                                subtitle:"Travel, hobbies, daily routines — what does freedom look like?",      placeholder:"I travel 3 months a year and have mornings free for myself...",   chips:["Travel the world freely","Mornings slow and intentional","Hobbies I'm passionate about","Time freedom — no schedule pressure","Investing in personal growth"] },
  { id:"spiritual",      emoji:"🙏", question:"What does your spiritual life look like?",                   subtitle:"Faith, peace, connection to God or the Universe.",                    placeholder:"I walk deeply in my faith and hear God's voice clearly...",      chips:["Deep daily prayer and meditation","Hearing God's voice clearly","Living in divine peace","Walking boldly in my purpose","Spiritual mentor to others"] },
  { id:"impact",         emoji:"🌍", question:"How are you impacting your community or the world?",         subtitle:"Who are you serving? What change are you creating?",                  placeholder:"I run a foundation that helps youth and mentors entrepreneurs...", chips:["Mentoring the next generation","Running a charity or foundation","Creating jobs and opportunity","Teaching and empowering others","Being a voice for the voiceless"] },
  { id:"accomplishments",emoji:"🏆", question:"What have you accomplished that once felt impossible?",      subtitle:"Your biggest breakthroughs and milestones — own them.",                placeholder:"I paid off all my debt and launched a successful business...",    chips:["Paid off all debt","Bought my dream home","Launched a successful business","Achieved financial freedom","Built something that outlasts me"] },
  { id:"legacy",         emoji:"🌱", question:"What legacy do you want to leave behind?",                   subtitle:"What will people remember about your life?",                          placeholder:"I want to be remembered as someone who loved deeply and changed lives...", chips:["Generational wealth for my family","A business that outlives me","Thousands of lives transformed","A love story worth telling","A community forever changed"] },
  { id:"feeling",        emoji:"☀️", question:"What is the one feeling you want to wake up with every day?",subtitle:"This is the emotional heartbeat of your dream life.",                placeholder:"I want to wake up every morning feeling peaceful, grateful, and free...", chips:["Completely free","Deeply at peace","Overflowing with gratitude","Powerful and unstoppable","Deeply loved and chosen","On fire with purpose"] },
];

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────
function useLocalStorage(key, initial) {
  const [val, setVal] = useState(() => { try { return JSON.parse(localStorage.getItem(key)) ?? initial; } catch { return initial; } });
  useEffect(() => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} }, [key, val]);
  return [val, setVal];
}
function getGreeting() { const h = new Date().getHours(); return h < 12 ? "Good Morning" : h < 17 ? "Good Afternoon" : "Good Evening"; }
function pickAffirmation(cats, exclude = "") {
  const pool = (cats||[]).flatMap(c => AFFIRMATIONS[c]||[]);
  const src = pool.length ? pool : Object.values(AFFIRMATIONS).flat();
  const list = src.filter(a => a !== exclude).length ? src.filter(a => a !== exclude) : src;
  return list[Math.floor(Math.random() * list.length)];
}
async function callClaude(system, user) {
  const res = await fetch("https://api.anthropic.com/v1/messages", {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ model: "claude-sonnet-4-20250514", max_tokens: 1000, system, messages: [{ role: "user", content: user }] }),
  });
  if (!res.ok) throw new Error("API error");
  const data = await res.json();
  return data.content?.[0]?.text?.trim() || "";
}

// ─────────────────────────────────────────────
// SHARED UI
// ─────────────────────────────────────────────
function Btn({ children, onClick, disabled, variant="green", style={} }) {
  const v = {
    green:  { background: disabled?`rgba(74,124,89,0.3)`:GRAD.green,  color:"white", boxShadow: disabled?"none":`inset 0 1px 0 rgba(255,255,255,0.16), 0 4px 20px rgba(44,74,46,0.35)` },
    gold:   { background: disabled?`rgba(201,168,76,0.3)`:GRAD.gold,  color:C.green, boxShadow: disabled?"none":`inset 0 1px 0 rgba(255,255,255,0.38), 0 4px 20px rgba(201,168,76,0.4)` },
    warm:   { background: disabled?`rgba(201,168,76,0.3)`:GRAD.gold,  color:C.green, boxShadow: disabled?"none":`inset 0 1px 0 rgba(255,255,255,0.38), 0 4px 20px rgba(201,168,76,0.35)` },
    ghost:  { background: `rgba(44,74,46,0.07)`, color:C.greenMid, boxShadow:"none", border:`1px solid rgba(44,74,46,0.18)` },
    dark:   { background: disabled?`rgba(28,46,30,0.3)`:GRAD.dark,   color:"white", boxShadow: disabled?"none":`0 4px 20px rgba(28,46,30,0.4)` },
    red:    { background: disabled?`rgba(180,80,80,0.3)`:"linear-gradient(135deg,#c87878,#e09090)", color:"white", boxShadow: disabled?"none":"0 4px 18px rgba(200,120,120,0.3)" },
    purple: { background: disabled?`rgba(122,92,168,0.3)`:"linear-gradient(135deg,#7a5ca8,#9b7fc8)", color:"white", boxShadow: disabled?"none":"0 4px 18px rgba(122,92,168,0.32)" },
  };
  return (
    <button onClick={disabled?undefined:(e)=>{buzz();onClick&&onClick(e);}} style={{
      width:"100%", border:"none", borderRadius:50, padding:"16px 24px",
      ...bs, fontSize:16, fontWeight:600, letterSpacing:"0.08em",
      cursor:disabled?"default":"pointer", opacity:disabled?0.5:1,
      transition:"all .2s", ...v[variant], ...style
    }}>{children}</button>
  );
}

// ── LOGO MARK (Crown + Heart SVG) ──
function LogoMark({ size=40, color=C.gold }) {
  return (
    <img
      src="https://ihart2manifest.com/logo.jpg"
      alt="iHart2Manifest"
      style={{ width:size, height:size, borderRadius:"50%", objectFit:"cover" }}
    />
  );
}


// ── ICON SYSTEM — consistent thin-stroke SVG icons ──
const ICON_PATHS = {
  sun:     <><circle cx="12" cy="12" r="4"/><line x1="12" y1="2.5" x2="12" y2="5"/><line x1="12" y1="19" x2="12" y2="21.5"/><line x1="2.5" y1="12" x2="5" y2="12"/><line x1="19" y1="12" x2="21.5" y2="12"/><line x1="5.3" y1="5.3" x2="7" y2="7"/><line x1="17" y1="17" x2="18.7" y2="18.7"/><line x1="18.7" y1="5.3" x2="17" y2="7"/><line x1="7" y1="17" x2="5.3" y2="18.7"/></>,
  sunrise: <><path d="M6 17a6 6 0 0 1 12 0"/><line x1="3" y1="20.5" x2="21" y2="20.5"/><line x1="12" y1="3" x2="12" y2="6.5"/><line x1="5" y1="6.5" x2="7.2" y2="8.7"/><line x1="19" y1="6.5" x2="16.8" y2="8.7"/></>,
  moon:    <path d="M20.5 13.2A8.2 8.2 0 1 1 10.8 3.5a6.4 6.4 0 0 0 9.7 9.7z"/>,
  spark:   <path d="M12 1.8c.9 5.9 3.4 8.4 9.3 10.2-5.9 1.8-8.4 4.3-9.3 10.2-.9-5.9-3.4-8.4-9.3-10.2 5.9-1.8 8.4-4.3 9.3-10.2z"/>,
  diamond: <><rect x="6.2" y="6.2" width="11.6" height="11.6" rx="2" transform="rotate(45 12 12)"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/></>,
  leaf:    <><path d="M5.5 18.5C5.5 9.5 11 4.5 20 4.5c0 9-5 14.5-14.5 14z"/><path d="M5.5 18.5c2.6-4.6 6.4-8.4 10.5-10.5"/></>,
  flame:   <path d="M12 2.5c1.1 3.9 5 5.6 5 9.7a5 5 0 0 1-10 0c0-1.9.9-3.4 1.9-4.9.4 1.2 1.1 2.1 2.1 2.6-.4-2.5 0-5.1 1-7.4z"/>,
  heart:   <path d="M12 20.2C6.4 16 3.5 12.6 3.5 9 3.5 6.4 5.5 4.5 8 4.5c1.6 0 3 .8 4 2.1 1-1.3 2.4-2.1 4-2.1 2.5 0 4.5 1.9 4.5 4.5 0 3.6-2.9 7-8.5 11.2z"/>,
  target:  <><circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="4.7"/><circle cx="12" cy="12" r="1.4" fill="currentColor" stroke="none"/></>,
  crown:   <path d="M4.2 16.5L3 7.8l4.9 3.1L12 4.5l4.1 6.4L21 7.8l-1.2 8.7H4.2zm0 2h15.6a.9.9 0 0 1 .9.9.9.9 0 0 1-.9.9H4.2a.9.9 0 0 1-.9-.9.9.9 0 0 1 .9-.9z"/>,
  lock:    <><rect x="5" y="10.5" width="14" height="9.5" rx="2.5"/><path d="M8 10.5V7.5a4 4 0 0 1 8 0v3"/></>,
  doc:     <><rect x="5.5" y="3.5" width="13" height="17" rx="2"/><line x1="9" y1="9" x2="15" y2="9"/><line x1="9" y1="12.5" x2="15" y2="12.5"/><line x1="9" y1="16" x2="12.5" y2="16"/></>,
  chat:    <path d="M20.5 11.5a8 8 0 0 1-8 8c-1.5 0-2.9-.4-4.1-1.1L3.5 19.5l1.1-4.7a8 8 0 1 1 15.9-3.3z"/>,
  gear:    <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1 1.55V21a2 2 0 1 1-4 0v-.09a1.7 1.7 0 0 0-1-1.55 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.7 1.7 0 0 0 .34-1.87 1.7 1.7 0 0 0-1.55-1H3a2 2 0 1 1 0-4h.09a1.7 1.7 0 0 0 1.55-1 1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34 1.7 1.7 0 0 0 1-1.55V3a2 2 0 1 1 4 0v.09a1.7 1.7 0 0 0 1 1.55 1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87 1.7 1.7 0 0 0 1.55 1H21a2 2 0 1 1 0 4h-.09a1.7 1.7 0 0 0-1.55 1z"/></>,
  star:    <path d="M12 3.2l2.6 5.4 5.9.8-4.3 4.1 1 5.9L12 16.6l-5.2 2.8 1-5.9-4.3-4.1 5.9-.8L12 3.2z"/>,
  share:   <><path d="M12 14.5V3.5"/><path d="M7.8 7.2L12 3l4.2 4.2"/><path d="M5 12.5V19a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6.5"/></>,
  refresh: <><path d="M20.5 12a8.5 8.5 0 1 1-2.5-6"/><path d="M20.8 3.2v5h-5"/></>,
  bell:    <><path d="M18 9.3a6 6 0 1 0-12 0c0 5.7-2.3 6.7-2.3 6.7h16.6S18 15 18 9.3z"/><path d="M10.2 19.7a2 2 0 0 0 3.6 0"/></>,
  camera:  <><rect x="3" y="7" width="18" height="13.5" rx="2.5"/><path d="M8.5 7l1.6-2.5h3.8L15.5 7"/><circle cx="12" cy="13.3" r="3.5"/></>,
  gift:    <><rect x="4" y="11" width="16" height="9.5" rx="1.5"/><rect x="3" y="7.5" width="18" height="3.5" rx="1"/><line x1="12" y1="7.5" x2="12" y2="20.5"/><path d="M12 7.5C12 4.2 9.6 2.9 8.3 3.7 6.9 4.6 7.8 7.5 12 7.5z"/><path d="M12 7.5c0-3.3 2.4-4.6 3.7-3.8 1.4.9.5 3.8-3.7 3.8z"/></>,
};
function Icon({ name, size=18, color="currentColor", style={} }) {
  const filled = name==="spark" || name==="crown";
  return (
    <svg width={size} height={size} viewBox="0 0 24 24"
      fill={filled ? color : "none"}
      stroke={filled ? "none" : color}
      strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"
      style={{ display:"block", flexShrink:0, ...style }}>
      {ICON_PATHS[name] || null}
    </svg>
  );
}

// ── HAPTIC FEEDBACK — Capacitor native when available, web vibration fallback ──
function buzz() {
  try {
    const H = window.Capacitor?.Plugins?.Haptics;
    if (H?.impact) { H.impact({ style: "LIGHT" }); return; }
    if (navigator.vibrate) navigator.vibrate(8);
  } catch {}
}

// ── NATIVE NOTIFICATIONS — Capacitor LocalNotifications when available, Web Notification API fallback ──
// Requires: npm install @capacitor/local-notifications && npx cap sync ios
const REMINDER_IDS = { morning: 1, midday: 2, evening: 3 };

function localNotifsPlugin() {
  try { return window.Capacitor?.isNativePlatform?.() ? (window.Capacitor?.Plugins?.LocalNotifications || null) : null; }
  catch { return null; }
}

// idle | granted | denied | unsupported
async function checkNotifPermission() {
  const LN = localNotifsPlugin();
  if (LN) {
    try { const res = await LN.checkPermissions(); return res.display === "granted" ? "granted" : "idle"; }
    catch { return "idle"; }
  }
  if (!("Notification" in window)) return "unsupported";
  return Notification.permission === "granted" ? "granted" : "idle";
}

async function requestNotifPermission() {
  const LN = localNotifsPlugin();
  if (LN) {
    try { const res = await LN.requestPermissions(); return res.display === "granted" ? "granted" : "denied"; }
    catch { return "denied"; }
  }
  if (!("Notification" in window)) return "unsupported";
  try { return await Notification.requestPermission(); } catch { return "denied"; }
}

// Schedules (or cancels, if turned off) one daily-repeating native reminder. No-ops on web —
// the web fallback path is handled inline at each call site since it has no persistent scheduler.
async function syncReminder(key, rem) {
  const LN = localNotifsPlugin();
  if (!LN) return;
  const id = REMINDER_IDS[key];
  try { await LN.cancel({ notifications: [{ id }] }); } catch {}
  if (!rem.on) return;
  try {
    await LN.schedule({ notifications: [{
      id,
      title: `iHart2Manifest — ${rem.label}`,
      body: rem.message,
      schedule: { on: { hour: rem.hour, minute: rem.minute } },
    }] });
  } catch {}
}

async function syncAllReminders(reminders) {
  for (const key of Object.keys(reminders)) await syncReminder(key, reminders[key]);
}

// ── ANIMATED COUNT-UP for stat numbers ──
function CountUp({ value, duration=750 }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!value) { setN(value || 0); return; }
    let start, raf;
    const step = (t) => {
      if (start === undefined) start = t;
      const p = Math.min((t - start) / duration, 1);
      setN(Math.round(value * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{n}</>;
}

function ProgressBar({ current, total }) {
  return (
    <div style={{ marginBottom:28 }}>
      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:8 }}>
        <span style={{ ...bs, fontSize:11, letterSpacing:"0.18em", textTransform:"uppercase", color:C.textSoft }}>Question {current} of {total}</span>
        <span style={{ ...bs, fontSize:11, color:C.goldDeep, fontWeight:600 }}>{Math.round((current/total)*100)}%</span>
      </div>
      <div style={{ height:6, background:"rgba(74,124,89,0.12)", borderRadius:3, overflow:"hidden" }}>
        <div style={{ height:"100%", borderRadius:3, background:GRAD.gold, width:`${(current/total)*100}%`, transition:"width .4s ease" }} />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// ONBOARDING — 3 steps, 5 questions
// ─────────────────────────────────────────────
const IDENTITY_WORDS = ["Blessed","Abundant","Unstoppable","Chosen","Healed","Worthy","Powerful","Free","Loved","Favored","Anointed","Limitless"];
const FEELINGS = ["Peaceful","Grateful","Powerful","Free","Loved","On Fire"];
const FEELING_EMOJIS = { Peaceful:"😌", Grateful:"🙏", Powerful:"💪", Free:"✈️", Loved:"❤️", "On Fire":"🔥" };

function OnboardingStep({ step, total, children }) {
  return (
    <div style={{ animation:"fadeInUp .4s ease" }}>
      {/* Progress */}
      <div style={{ display:"flex", gap:6, justifyContent:"center", marginBottom:36 }}>
        {[...Array(total)].map((_,i) => (
          <div key={i} style={{
            height:6, borderRadius:3,
            width: i===step-1 ? 32 : 10,
            background: i<step ? GRAD.gold : "rgba(201,168,76,0.18)",
            transition:"all .35s",
          }}/>
        ))}
      </div>
      {children}
    </div>
  );
}

function Onboarding({ onComplete }) {
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [categories, setCategories] = useState([]);

  const [identityWords, setIdentityWords] = useState([]);
  const [feeling, setFeeling] = useState("");

  const toggleCat = c => setCategories(s => s.includes(c) ? s.filter(x=>x!==c) : [...s,c]);
  const toggleWord = w => setIdentityWords(s => s.includes(w) ? s.filter(x=>x!==w) : s.length<3 ? [...s,w] : s);

  const inputStyle = {
    width:"100%", boxSizing:"border-box",
    border:`1.5px solid rgba(44,74,46,0.2)`, borderRadius:16,
    padding:"15px 18px", ...bs, fontSize:16,
    background:"rgba(255,255,255,0.9)", color:C.green,
    outline:"none", boxShadow:`0 2px 12px rgba(44,74,46,0.07)`,
  };

  const [desireStatement, setDesireStatement] = useState("");

  const finish = () => {
    const dream = `I Am ${identityWords.join(", ")}. I Desire ${desireStatement.trim()}. Every Day I Wake Up Feeling ${feeling}.`;
    onComplete({ name: name.trim(), categories, dream, identityWords, feeling });
  };

  return (
    <div style={{
      minHeight:"100vh", padding:"52px 24px 48px",
      background: GRAD.bg,
      display:"flex", flexDirection:"column", justifyContent:"center",
      overflowY:"auto",
    }}>
      <link rel="stylesheet" href={FONTS}/>

      {/* ── STEP 1: Name ── */}
      {step===1 && (
        <OnboardingStep step={1} total={3}>
          <div style={{ textAlign:"center", marginBottom:32 }}>
            <div style={{ display:"flex", justifyContent:"center", marginBottom:4 }}><LogoMark size={56} color={C.gold}/></div>
            <h1 style={{ ...ds, fontSize:28, color:C.green, margin:"16px 0 6px" }}>
              Welcome to<br/><span style={{ color:C.gold }}>iHart2Manifest</span>
            </h1>
            <p style={{ ...bs, color:C.textSoft, fontSize:14, margin:0, fontStyle:"italic", lineHeight:1.6 }}>
              YOUR WORDS HAVE POWER. It's Time to Use Them.
            </p>
          </div>
          <label style={{ ...bs, fontSize:11, letterSpacing:"0.2em", color:C.textSoft, textTransform:"uppercase", display:"block", marginBottom:10 }}>
            What's your name?
          </label>
          <input value={name} onChange={e=>setName(e.target.value)}
            onKeyDown={e=>e.key==="Enter"&&name.trim()&&setStep(2)}
            placeholder="Enter your first name..."
            maxLength={30} style={{ ...inputStyle, textAlign:"center", fontSize:18 }}/>
          <div style={{ marginTop:22 }}>
            <Btn disabled={!name.trim()} onClick={()=>setStep(2)}>Continue →</Btn>
          </div>
        </OnboardingStep>
      )}

      {/* ── STEP 2: Focus + Desire ── */}
      {step===2 && (
        <OnboardingStep step={2} total={3}>
          <h2 style={{ ...ds, fontSize:24, color:C.green, margin:"0 0 6px" }}>
            What are you growing in?
          </h2>
          <p style={{ ...bs, color:C.textSoft, fontSize:14, fontStyle:"italic", margin:"0 0 20px" }}>
            Select your focus areas, {name} ✦
          </p>

          {/* Categories */}
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:9, marginBottom:22 }}>
            {CATEGORIES.map(c => {
              const m = CAT_META[c]; const on = categories.includes(c);
              return (
                <button key={c} onClick={()=>toggleCat(c)} style={{
                  border: on ? `2px solid ${m.color}` : `1.5px solid rgba(201,168,76,0.18)`,
                  borderRadius:16, padding:"16px 10px",
                  background: on ? m.light : "rgba(255,255,255,0.88)",
                  cursor:"pointer", transition:"all .18s",
                  boxShadow: on ? `0 4px 14px ${m.color}28` : `0 2px 10px rgba(201,168,76,0.08)`,
                  display:"flex", flexDirection:"column", alignItems:"center", gap:6,
                }}>
                  <span style={{ width:30, height:30, borderRadius:"50%", border:`1.5px solid ${m.color}66`, display:"flex", alignItems:"center", justifyContent:"center" }}><span style={{ width:10, height:10, borderRadius:"50%", background:m.color }}/></span>
                  <span style={{ ...bs, fontSize:13, fontWeight:600, color:on?m.color:C.textMid }}>{c}</span>
                  {on && <span style={{ fontSize:9, color:m.color, letterSpacing:"0.1em" }}>✓ SELECTED</span>}
                </button>
              );
            })}
          </div>



          <div style={{ display:"flex", gap:10, marginTop:18 }}>
            <Btn variant="ghost" onClick={()=>setStep(1)} style={{ flex:"0 0 80px" }}>← Back</Btn>
            <div style={{ flex:1 }}>
              <Btn disabled={categories.length===0} onClick={()=>setStep(3)}>Continue →</Btn>
            </div>
          </div>
        </OnboardingStep>
      )}

      {/* ── STEP 3: Identity + Feeling ── */}
      {step===3 && (
        <OnboardingStep step={3} total={3}>
          <h2 style={{ ...ds, fontSize:24, color:C.green, margin:"0 0 6px" }}>
            Speak Your Declaration
          </h2>
          <p style={{ ...bs, color:C.textSoft, fontSize:14, fontStyle:"italic", margin:"0 0 20px" }}>
            Complete your identity declaration, {name} ✦
          </p>

          {/* Identity words */}
          <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>I Am...</p>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:6 }}>
            {IDENTITY_WORDS.map(w => {
              const on = identityWords.includes(w);
              const full = identityWords.length>=3 && !on;
              return (
                <button key={w} onClick={()=>toggleWord(w)} disabled={full} style={{
                  border: on ? `2px solid ${C.gold}` : `1.5px solid rgba(201,168,76,0.25)`,
                  borderRadius:20, padding:"8px 14px",
                  background: on ? `rgba(201,168,76,0.15)` : "rgba(255,255,255,0.8)",
                  color: on ? C.gold : full ? "rgba(100,100,100,0.35)" : C.textMid,
                  cursor: full ? "default" : "pointer",
                  ...bs, fontSize:12, fontWeight: on ? 700 : 400,
                  transition:"all .15s",
                }}>
                  {on ? "✓ " : ""}{w}
                </button>
              );
            })}
          </div>
          <p style={{ ...bs, fontSize:11, color:identityWords.length<3?C.textSoft:C.gold, fontStyle:"italic", margin:"0 0 18px" }}>
            {identityWords.length < 3 ? `Pick ${3-identityWords.length} more word${3-identityWords.length!==1?"s":""}` : `✦ I Am ${identityWords.join(", ")}`}
          </p>

          {/* I Desire */}
          <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>I Desire...</p>
          <div style={{ position:"relative", marginBottom:18 }}>
            <textarea
              value={desireStatement}
              onChange={e=>setDesireStatement(e.target.value)}
              placeholder="e.g. financial freedom, a thriving business and a life full of purpose..."
              rows={3}
              style={{ width:"100%", boxSizing:"border-box", border:`1.5px solid rgba(44,74,46,0.2)`, borderRadius:16, padding:"14px 18px", ...bs, fontSize:15, background:"rgba(255,255,255,0.9)", color:C.green, outline:"none", resize:"none", lineHeight:1.6 }}
            />
          </div>

          {/* Feeling */}
          <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>Every Day I Wake Up Feeling...</p>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:20 }}>
            {FEELINGS.map(f => {
              const on = feeling===f;
              return (
                <button key={f} onClick={()=>setFeeling(f)} style={{
                  border: on ? `2px solid ${C.gold}` : `1.5px solid rgba(201,168,76,0.18)`,
                  borderRadius:14, padding:"16px 10px",
                  background: on ? `rgba(201,168,76,0.12)` : "rgba(255,255,255,0.85)",
                  cursor:"pointer", transition:"all .15s",
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:8,
                  boxShadow: on ? `0 2px 10px rgba(201,168,76,0.2)` : "0 1px 6px rgba(0,0,0,0.04)",
                  position:"relative",
                }}>
                  <span style={{ fontSize:26 }}>{FEELING_EMOJIS[f]}</span>
                  <span style={{ ...bs, fontSize:13, fontWeight: on?700:500, color: on?C.gold:C.textMid, textAlign:"center" }}>{f}</span>
                  {on && <div style={{ position:"absolute", top:8, right:8, width:16, height:16, borderRadius:"50%", background:GRAD.gold, display:"flex", alignItems:"center", justifyContent:"center" }}><span style={{ fontSize:9, color:C.green, fontWeight:700 }}>✓</span></div>}
                </button>
              );
            })}
          </div>

          {/* Live preview */}
          {identityWords.length===3 && desireStatement.trim() && feeling && (
            <div style={{ background:`linear-gradient(135deg,rgba(44,74,46,0.95),rgba(28,46,30,0.98))`, borderRadius:18, padding:"16px 18px", marginBottom:18, border:`1px solid rgba(201,168,76,0.2)`, animation:"fadeInUp .4s ease" }}>
              <p style={{ ...bs, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:`rgba(201,168,76,0.6)`, margin:"0 0 10px" }}>Your Declaration</p>
              <p style={{ ...ds, fontSize:15, color:"rgba(255,255,255,0.92)", margin:0, lineHeight:1.75, fontStyle:"italic" }}>
                "I Am {identityWords.join(", ")}. I Desire {desireStatement.trim()}. Every Day I Wake Up Feeling {feeling}."
              </p>
            </div>
          )}

          <div style={{ display:"flex", gap:10 }}>
            <Btn variant="ghost" onClick={()=>setStep(2)} style={{ flex:"0 0 80px" }}>← Back</Btn>
            <div style={{ flex:1 }}>
              <Btn disabled={identityWords.length<3||!desireStatement.trim()||!feeling} onClick={finish} variant="gold" style={{ opacity: identityWords.length<3||!desireStatement.trim()||!feeling ? 0.45 : 1 }}>
                ✦ Begin My Journey
              </Btn>
            </div>
          </div>
          <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
        </OnboardingStep>
      )}

      <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// AHA MOMENT — Welcome screen after onboarding
// ─────────────────────────────────────────────
function AhaMoment({ profile, onEnter }) {
  const firstAffirmation = pickAffirmation(profile.categories);
  const [visible, setVisible] = useState(false);

  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <div style={{
      minHeight:"100vh", background:GRAD.dark,
      display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      padding:"40px 28px",
      opacity: visible ? 1 : 0, transition:"opacity 0.8s ease",
    }}>
      <link rel="stylesheet" href={FONTS}/>

      {/* Logo */}
      <div style={{ marginBottom:28, animation:"floatIn 1s ease" }}>
        <LogoMark size={64} color={C.gold}/>
      </div>

      {/* Welcome message */}
      <p style={{ ...bs, fontSize:12, letterSpacing:"0.22em", textTransform:"uppercase", color:`rgba(201,168,76,0.6)`, margin:"0 0 10px", textAlign:"center" }}>
        Welcome,
      </p>
      <h1 style={{ ...ds, fontSize:34, color:C.gold, margin:"0 0 6px", textAlign:"center", lineHeight:1.2 }}>
        {profile.name}
      </h1>

      {/* Identity declaration */}
      <p style={{ ...ds, fontSize:17, color:"rgba(255,255,255,0.7)", margin:"0 0 36px", textAlign:"center", fontStyle:"italic", lineHeight:1.6 }}>
        I Am {profile.identityWords?.join(", ")} &amp; I feel {profile.feeling?.toLowerCase()} every day.
      </p>

      {/* Gold divider */}
      <div style={{ width:60, height:1, background:GRAD.gold, marginBottom:36, borderRadius:1 }}/>

      {/* First affirmation */}
      <p style={{ ...bs, fontSize:11, letterSpacing:"0.2em", textTransform:"uppercase", color:`rgba(201,168,76,0.5)`, margin:"0 0 16px", textAlign:"center" }}>
        Your First Affirmation
      </p>
      <div style={{
        background:"rgba(255,255,255,0.06)", borderRadius:24,
        padding:"28px 24px", border:`1px solid rgba(201,168,76,0.15)`,
        marginBottom:36, backdropFilter:"blur(10px)",
        boxShadow:`0 8px 32px rgba(0,0,0,0.2)`,
      }}>
        <p style={{ ...ds, fontSize:19, color:"rgba(255,255,255,0.92)", margin:0, textAlign:"center", fontStyle:"italic", lineHeight:1.75 }}>
          "{firstAffirmation}"
        </p>
      </div>

      <Btn onClick={onEnter} variant="gold">
        ✦ Enter My Sacred Space
      </Btn>

      <style>{`
        @keyframes floatIn { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// AFFIRMATIONS TAB
// ─────────────────────────────────────────────
function AffirmationsTab({ profile, onReset, onUpdateProfile, isPremium, onUpgrade, devMode, onToggleDev, onTogglePremium, daysLeft, isTrialActive }) {
  const [affirmation, setAffirmation] = useLocalStorage("iHM_aff","");
  const [isAI, setIsAI] = useLocalStorage("iHM_isAI",false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState("");
  const [streak, setStreak] = useLocalStorage("iHM_streak",{count:0,last:""});
  const [showDream, setShowDream] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [editName, setEditName] = useState(profile.name||"");
  const [editCats, setEditCats] = useState(profile.categories||[]);
  const [showAbout, setShowAbout] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [logoTapCount, setLogoTapCount] = useState(0);
  // Progress stats
  const [gratitude] = useLocalStorage("iHM_gratitude", {});
  const [iamList] = useLocalStorage("iHM_iam", []);
  const [goals] = useLocalStorage("iHM_goals", []);
  const gratitudeDays = Object.values(gratitude).filter(d => d.some(x=>x)).length;
  const goalsCompleted = goals.filter(g=>g.done).length;
  const last7 = [...Array(7)].map((_,i)=>{ const d=new Date(); d.setDate(d.getDate()-i); return d.toISOString().slice(0,10); }).reverse();

  useEffect(()=>{ if(!affirmation){setAffirmation(pickAffirmation(profile.categories));setIsAI(false);} },[]);
  useEffect(()=>{
    const today=new Date().toISOString().slice(0,10);
    if(streak.last!==today){ const yday=new Date(Date.now()-86400000).toISOString().slice(0,10); setStreak({count:streak.last===yday?streak.count+1:1,last:today}); }
  },[]);

  const getNew = useCallback(()=>{ setAffirmation(pickAffirmation(profile.categories,affirmation)); setIsAI(false); setAiError(""); },[affirmation,profile.categories]);

  const today = new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"});
  return (
    <div style={{ padding:"0 20px 100px", background:GRAD.bg, minHeight:"100vh" }}>
      {showReset && (
        <div style={{ position:"fixed", inset:0, zIndex:300, background:"rgba(20,35,25,0.82)", backdropFilter:"blur(8px)", display:"flex", alignItems:"flex-end" }}>
          <div style={{ background:C.bg, borderRadius:"24px 24px 0 0", padding:"28px 24px 48px", width:"100%", boxSizing:"border-box", maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
              <div onClick={()=>{ const n=logoTapCount+1; setLogoTapCount(n); if(n>=7){onToggleDev();setLogoTapCount(0);} setTimeout(()=>setLogoTapCount(0), 3000); }} style={{ cursor:"default" }}>
                <h3 style={{ ...ds, fontSize:22, color:C.green, margin:0 }}>
                  <span style={{ display:"inline-flex", alignItems:"center", gap:8, verticalAlign:"middle" }}><Icon name="gear" size={18} color={C.green}/>Settings</span> {logoTapCount>=3 && logoTapCount<7 ? <span style={{ fontSize:13, color:"rgba(155,127,200,0.7)", fontFamily:"monospace" }}>({logoTapCount}/7)</span> : ""}
                </h3>
              </div>
              <button onClick={()=>setShowReset(false)} style={{ background:`rgba(44,74,46,0.08)`, border:"none", borderRadius:"50%", width:32, height:32, cursor:"pointer", fontSize:18, color:C.green }}>×</button>
            </div>

            {/* Premium Status */}
            <div style={{ background:isPremium?`linear-gradient(135deg,rgba(44,74,46,0.95),rgba(28,46,30,0.98))`:isTrialActive?`linear-gradient(135deg,rgba(44,74,46,0.7),rgba(28,46,30,0.8))`:`rgba(201,168,76,0.06)`, borderRadius:18, padding:"16px 18px", marginBottom:20, border:`1px solid ${isPremium||isTrialActive?"rgba(201,168,76,0.3)":C.border}` }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <p style={{ ...ds, fontSize:15, color:isPremium||isTrialActive?C.gold:C.green, margin:"0 0 2px", fontWeight:600 }}>
                    {isPremium? <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="crown" size={14} color={C.gold}/>Premium Member</span> : isTrialActive? <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><Icon name="gift" size={13} color={C.gold}/>{`Free Trial — ${daysLeft} day${daysLeft!==1?"s":""} left`}</span> : "Free Plan"}
                  </p>
                  <p style={{ ...bs, fontSize:12, color:isPremium||isTrialActive?"rgba(201,168,76,0.6)":C.textSoft, margin:0, fontStyle:"italic" }}>
                    {isPremium?"All features unlocked":isTrialActive?"Full access — upgrade before trial ends":"Upgrade for full access"}
                  </p>
                </div>
                {!isPremium && <button onClick={()=>{setShowReset(false);setShowUpgradeModal(true);}} style={{ border:`1.5px solid ${C.gold}`, borderRadius:20, padding:"7px 16px", background:`rgba(201,168,76,0.1)`, color:C.gold, cursor:"pointer", ...bs, fontSize:12, fontWeight:700 }}>{isTrialActive?"Upgrade ✦":"Upgrade ✦"}</button>}
              </div>
            </div>

            {/* Profile */}
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 8px" }}>Your Name</p>
            <input value={editName} onChange={e=>setEditName(e.target.value)}
              style={{ width:"100%", boxSizing:"border-box", border:`1.5px solid ${C.border}`, borderRadius:14, padding:"12px 16px", ...bs, fontSize:16, background:"rgba(255,255,255,0.9)", color:C.green, outline:"none", marginBottom:16 }}/>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>Focus Areas</p>
            <div style={{ display:"flex", flexWrap:"wrap", gap:7, marginBottom:20 }}>
              {CATEGORIES.map(c => {
                const m=CAT_META[c]; const on=editCats.includes(c);
                return <button key={c} onClick={()=>setEditCats(s=>s.includes(c)?s.filter(x=>x!==c):[...s,c])} style={{ border:on?`1.5px solid ${m.color}`:`1.5px solid ${C.border}`, borderRadius:20, padding:"6px 14px", background:on?m.light:"rgba(255,255,255,0.8)", color:on?m.color:C.textMid, cursor:"pointer", ...bs, fontSize:12, fontWeight:600 }}><span style={{ width:6, height:6, borderRadius:"50%", background:m.color, display:"inline-block", marginRight:7, verticalAlign:"1px", flexShrink:0 }}/>{c}</button>;
              })}
            </div>
            <Btn variant="green" onClick={()=>{ onUpdateProfile({name:editName.trim()||profile.name, categories:editCats.length?editCats:profile.categories}); setShowReset(false); }} style={{ marginBottom:20 }}>✦ Save Changes</Btn>

            {/* About & Support */}
            <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:20, marginBottom:16 }}>
              <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 12px" }}>About & Support</p>
              {[
                { icon:"crown", label:"About iHart2Manifest", action:()=>setShowAbout(true) },
                { icon:"lock", label:"Privacy Policy", action:()=>setShowPrivacy(true) },
                { icon:"doc", label:"Terms of Use", action:()=>setShowTerms(true) },
                { icon:"chat", label:"Contact Support", action:()=>window.open("mailto:support@ihart2manifest.com?subject=Support Request","_blank") },
                { icon:"star", label:"Rate the App", action:()=>window.open("https://apps.apple.com","_blank") },
              ].map((item,i) => (
                <button key={i} onClick={item.action} style={{ width:"100%", display:"flex", alignItems:"center", gap:12, padding:"13px 16px", background:"rgba(255,255,255,0.7)", border:`1px solid ${C.border}`, borderRadius:14, cursor:"pointer", marginBottom:8, textAlign:"left" }}>
                  <Icon name={item.icon} size={17} color={C.greenMid}/>
                  <span style={{ ...bs, fontSize:14, color:C.green, fontWeight:500 }}>{item.label}</span>
                  <span style={{ marginLeft:"auto", color:C.textSoft, fontSize:14 }}>→</span>
                </button>
              ))}
            </div>

            {/* Dev Mode — hidden, tap logo 5x to reveal */}
            {devMode && (
              <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:16, marginBottom:16 }}>
                <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:"#9b7fc8", margin:"0 0 12px" }}>Developer Mode</p>
                <button onClick={()=>{onTogglePremium();}} style={{ width:"100%", border:"1.5px solid #9b7fc8", borderRadius:14, padding:"12px", background:"rgba(155,127,200,0.08)", color:"#9b7fc8", cursor:"pointer", ...bs, fontSize:13, fontWeight:700, marginBottom:8 }}>
                  {isPremium?"Switch to Free User":"Switch to Premium User"}
                </button>
                <button onClick={onToggleDev} style={{ width:"100%", border:"1px solid rgba(155,127,200,0.3)", borderRadius:14, padding:"10px", background:"transparent", color:"rgba(155,127,200,0.5)", cursor:"pointer", ...bs, fontSize:12 }}>
                  Exit Developer Mode
                </button>
              </div>
            )}

            {/* Reset */}
            <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:16 }}>
              <p style={{ ...bs, fontSize:13, color:C.textSoft, textAlign:"center", marginBottom:12, fontStyle:"italic" }}>Want to start completely fresh?</p>
              <Btn variant="red" onClick={()=>{setShowReset(false);onReset();}}>Reset Everything</Btn>
            </div>
          </div>
        </div>
      )}

      {/* About modal */}
      {showAbout && (
        <div style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(10,20,12,0.92)", backdropFilter:"blur(12px)", display:"flex", alignItems:"center", justifyContent:"center", padding:24 }}>
          <div style={{ background:`linear-gradient(160deg,${C.bg},${C.bgDeep})`, borderRadius:28, padding:"36px 24px", width:"100%", maxWidth:380, boxSizing:"border-box", textAlign:"center" }}>
            <LogoMark size={56} color={C.gold}/>
            <h2 style={{ ...ds, fontSize:24, color:C.green, margin:"16px 0 6px" }}>iHart2Manifest</h2>
            <p style={{ ...bs, fontSize:13, color:C.gold, letterSpacing:"0.15em", textTransform:"uppercase", margin:"0 0 16px" }}>YOUR WORDS HAVE POWER. It's Time to Use Them.</p>
            <div style={{ width:40, height:1, background:GRAD.gold, margin:"0 auto 20px", borderRadius:1 }}/>
            <p style={{ ...bs, fontSize:14, color:C.textMid, lineHeight:1.7, margin:"0 0 16px" }}>
              iHart2Manifest is a daily manifestation and affirmation app built on faith, intention, and the power of declaration. Created by iHart Enterprises to help you speak your dream life into existence.
            </p>
            <p style={{ ...bs, fontSize:12, color:C.textSoft, fontStyle:"italic", margin:"0 0 24px" }}>Version 1.0 · Built with love ✦</p>
            <Btn variant="green" onClick={()=>setShowAbout(false)}>Close</Btn>
          </div>
        </div>
      )}

      {/* Privacy Policy modal */}
      {showPrivacy && (
        <div style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(10,20,12,0.92)", backdropFilter:"blur(12px)", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"52px 20px 0", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h3 style={{ ...ds, fontSize:20, color:C.gold, margin:0 }}>Privacy Policy</h3>
            <button onClick={()=>setShowPrivacy(false)} style={{ background:"rgba(255,255,255,0.08)", border:"none", borderRadius:"50%", width:34, height:34, cursor:"pointer", fontSize:18, color:"white" }}>×</button>
          </div>
          <div style={{ padding:"0 20px 40px", overflowY:"auto", flex:1 }}>
            {[
              ["Data Collection", "iHart2Manifest stores all your data locally on your device. We do not collect, transmit, or sell any personal information to third parties."],
              ["What We Store", "Your name, focus areas, affirmations, vision board images, gratitude entries, goals, and check-ins are stored only on your device using local storage."],
              ["Notifications", "If you enable notifications, these are handled entirely by your device and browser. We do not store notification data on any server."],
              ["Payments", "Premium subscriptions are processed securely through Stripe or Apple. We never see or store your payment information."],
              ["Contact", "Questions? Email us at support@ihart2manifest.com"],
            ].map(([title, body], i) => (
              <div key={i} style={{ marginBottom:20 }}>
                <p style={{ ...ds, fontSize:15, color:C.gold, margin:"0 0 6px" }}>{title}</p>
                <p style={{ ...bs, fontSize:13, color:"rgba(255,255,255,0.7)", margin:0, lineHeight:1.7 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Terms modal */}
      {showTerms && (
        <div style={{ position:"fixed", inset:0, zIndex:400, background:"rgba(10,20,12,0.92)", backdropFilter:"blur(12px)", display:"flex", flexDirection:"column" }}>
          <div style={{ padding:"52px 20px 0", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <h3 style={{ ...ds, fontSize:20, color:C.gold, margin:0 }}>Terms of Use</h3>
            <button onClick={()=>setShowTerms(false)} style={{ background:"rgba(255,255,255,0.08)", border:"none", borderRadius:"50%", width:34, height:34, cursor:"pointer", fontSize:18, color:"white" }}>×</button>
          </div>
          <div style={{ padding:"0 20px 40px", overflowY:"auto", flex:1 }}>
            {[
              ["Acceptance", "By using iHart2Manifest, you agree to these terms. If you do not agree, please discontinue use of the app."],
              ["Use of App", "iHart2Manifest is intended for personal motivation, faith, and wellness purposes only. It is not a substitute for professional medical, psychological, or financial advice."],
              ["Premium Subscription", "Premium features require a paid subscription. Subscriptions auto-renew unless cancelled. You may cancel at any time."],
              ["Content", "You are responsible for the content you add to your vision board and declarations. Do not add unlawful or harmful content."],
              ["Limitation of Liability", "iHart Enterprises is not liable for any outcomes resulting from use of this app. All affirmations and declarations are for motivational purposes only."],
              ["Contact", "Questions? Email us at support@ihart2manifest.com"],
            ].map(([title, body], i) => (
              <div key={i} style={{ marginBottom:20 }}>
                <p style={{ ...ds, fontSize:15, color:C.gold, margin:"0 0 6px" }}>{title}</p>
                <p style={{ ...bs, fontSize:13, color:"rgba(255,255,255,0.7)", margin:0, lineHeight:1.7 }}>{body}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {showUpgradeModal && <UpgradeModal featureName={null} onClose={()=>setShowUpgradeModal(false)} onUpgrade={()=>{onUpgrade();setShowUpgradeModal(false);}}/>}
      <div style={{ paddingTop:56 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:6 }}>
          <div>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.22em", color:C.textSoft, textTransform:"uppercase", margin:"0 0 4px" }}>{today}</p>
            <h1 style={{ ...ds, fontSize:28, color:C.green, margin:"0 0 4px", lineHeight:1.15 }}>{getGreeting()},<br/>{profile.name} ✦</h1>
          </div>
          <button onClick={()=>{ setEditName(profile.name||""); setEditCats(profile.categories||[]); setShowReset(true); }} style={{ marginTop:4, background:`rgba(201,168,76,0.08)`, border:`1px solid rgba(201,168,76,0.25)`, borderRadius:12, padding:"8px 14px", cursor:"pointer", ...bs, fontSize:13, color:C.goldDeep, fontWeight:600, flexShrink:0, display:"flex", alignItems:"center", gap:6 }}><Icon name="gear" size={13}/>Settings</button>

        </div>

        {/* ── STATS BAR — hide on day 1 ── */}
        {(streak.count > 1 || gratitudeDays > 0 || iamList.length > 0 || goalsCompleted > 0) ? (
        <div style={{ background:`linear-gradient(135deg, rgba(44,74,46,0.96), rgba(28,46,30,0.98))`, borderRadius:18, padding:"14px 18px", marginBottom:20, boxShadow:`0 4px 20px rgba(28,46,30,0.2)` }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            {[
              { icon:"flame", value: streak.count > 0 ? streak.count : 0, label:"Day Streak" },
              { icon:"heart", value: gratitudeDays, label:"Gratitude" },
              { icon:"spark", value: iamList.length, label:"I Am's" },
              { icon:"target", value: goalsCompleted, label:"Goals Done" },
            ].map((s,i) => (
              <div key={i} style={{ flex:1, textAlign:"center", borderRight: i<3 ? "1px solid rgba(201,168,76,0.15)" : "none", paddingRight: i<3?8:0, paddingLeft: i>0?8:0 }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:5 }}><Icon name={s.icon} size={15} color={C.goldLight}/></div>
                <p style={{ ...ds, fontSize:20, color:C.gold, margin:"0 0 1px", fontWeight:700, lineHeight:1 }}><CountUp value={s.value}/></p>
                <p style={{ ...bs, fontSize:9, color:"rgba(201,168,76,0.6)", margin:0, letterSpacing:"0.1em", textTransform:"uppercase" }}>{s.label}</p>
              </div>
            ))}
          </div>

          {/* 7-day activity dots */}
          <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid rgba(201,168,76,0.12)" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              {last7.map((d,i) => {
                const hasCheckin = !!gratitude[d]?.some(x=>x);
                const isToday = d === new Date().toISOString().slice(0,10);
                const dayLabel = new Date(d+"T12:00:00").toLocaleDateString("en-US",{weekday:"short"}).slice(0,1);
                return (
                  <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                    <div style={{
                      width:28, height:28, borderRadius:"50%",
                      background: hasCheckin ? GRAD.gold : "rgba(201,168,76,0.1)",
                      border: isToday ? `2px solid ${C.gold}` : "2px solid rgba(201,168,76,0.2)",
                      display:"flex", alignItems:"center", justifyContent:"center",
                      boxShadow: hasCheckin ? `0 2px 8px rgba(201,168,76,0.4)` : "none",
                      transition:"all .3s",
                    }}>
                      {hasCheckin && <span style={{ fontSize:12 }}>✓</span>}
                    </div>
                    <span style={{ ...bs, fontSize:9, color:"rgba(201,168,76,0.5)", letterSpacing:"0.05em" }}>{dayLabel}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        ) : (
          <div style={{ background:`linear-gradient(135deg, rgba(44,74,46,0.96), rgba(28,46,30,0.98))`, borderRadius:18, padding:"18px 20px", marginBottom:20, boxShadow:`0 4px 20px rgba(28,46,30,0.2)`, textAlign:"center" }}>
            <p style={{ fontSize:28, margin:"0 0 8px" }}>✦</p>
            <p style={{ ...ds, fontSize:18, color:C.gold, margin:"0 0 6px", fontWeight:600 }}>Day 1 — Your journey begins today</p>
            <p style={{ ...bs, fontSize:13, color:"rgba(255,255,255,0.55)", margin:0, fontStyle:"italic" }}>Speak your affirmation. Declare your truth. Give thanks tonight.</p>
          </div>
        )}
      </div>

      <div style={{ display:"flex", gap:7, flexWrap:"wrap", marginBottom:20 }}>
        {profile.categories.map(c=>{ const m=CAT_META[c]; return <span key={c} style={{ background:m.light, color:m.color, border:`1px solid ${m.color}38`, borderRadius:20, padding:"5px 13px", ...bs, fontSize:12, fontWeight:600 }}><span style={{ width:6, height:6, borderRadius:"50%", background:m.color, display:"inline-block", marginRight:7, verticalAlign:"1px", flexShrink:0 }}/>{c}</span>; })}
      </div>
      <div style={{ background:"rgba(255,255,255,0.84)", backdropFilter:"blur(14px)", borderRadius:28, padding:"36px 26px 32px", border:`1px solid rgba(201,168,76,0.3)`, boxShadow:"0 8px 40px rgba(45,74,53,0.10), inset 0 1px 0 rgba(255,255,255,0.9)", position:"relative", overflow:"hidden", minHeight:170, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
        <div style={{ position:"absolute", top:0, left:"12%", right:"12%", height:3, background:isAI?"linear-gradient(90deg,#7a5ca8,#9b7fc8)":GRAD.gold, borderRadius:"0 0 4px 4px" }} />
        {aiLoading ? (
          <div style={{ textAlign:"center" }}>
            <div style={{ display:"flex", justifyContent:"center", gap:8, marginBottom:14 }}>{[0,1,2].map(i=><div key={i} style={{ width:9, height:9, borderRadius:"50%", background:"#4a7c59", animation:`dotPulse 1.3s ease-in-out ${i*0.22}s infinite` }}/>)}</div>
            <p style={{ ...bs, color:C.textSoft, fontSize:14, fontStyle:"italic", margin:0 }}>Channeling your affirmation...</p>
          </div>
        ) : (
          <>{isAI && <span style={{ ...bs, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"#9b7fc8", marginBottom:14, fontWeight:600 }}>✦ AI · Personalized for You</span>}
          <p style={{ ...ds, fontSize:20, lineHeight:1.78, color:C.green, margin:0, textAlign:"center", fontStyle:"italic", fontWeight:500 }}>"{affirmation}"</p></>
        )}
      </div>
      <div style={{ display:"flex", gap:10, marginTop:22 }}>
        <Btn onClick={getNew} variant="green"><span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:9 }}><Icon name="refresh" size={15}/>New Affirmation</span></Btn>
        <button onClick={()=>setShowShare(true)} style={{ flexShrink:0, width:52, height:52, borderRadius:16, border:`1.5px solid ${C.gold}`, background:`rgba(201,168,76,0.08)`, cursor:"pointer", fontSize:18, color:C.gold, display:"flex", alignItems:"center", justifyContent:"center" }}><Icon name="share" size={18}/></button>
      </div>
      {showShare && <ShareModal profile={profile} defaultType="affirmation" onClose={()=>setShowShare(false)}/>}
      <div onClick={()=>setShowDream(s=>!s)} style={{ marginTop:32, background:"rgba(255,255,255,0.62)", backdropFilter:"blur(8px)", borderRadius:20, padding:"18px 20px", border:`1px solid ${C.border}`, cursor:"pointer" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:0 }}>✦ My Dream Life Statement</p>
          <span style={{ color:C.textSoft, fontSize:13 }}>{showDream?"▲":"▼"}</span>
        </div>
        {showDream && (
          <>
            {profile.dream && profile.dream.length > 20 ? (
              <p style={{ ...ds, fontSize:15, color:C.green, margin:"14px 0 8px", lineHeight:1.75, fontStyle:"italic" }}>
                "{profile.dream}"
              </p>
            ) : (
              <p style={{ ...bs, fontSize:13, color:C.textSoft, margin:"12px 0 8px", fontStyle:"italic", lineHeight:1.6 }}>
                You haven't built your full dream statement yet. Go to Journey → Goals to create one.
              </p>
            )}
            {profile.dream && profile.dream.toLowerCase().startsWith("i am ") && profile.dream.toLowerCase().includes("i desire") && (
              <div style={{ background:`rgba(201,168,76,0.08)`, borderRadius:12, padding:"10px 14px", border:`1px solid rgba(201,168,76,0.2)`, marginTop:4 }}>
                <p style={{ ...bs, fontSize:12, color:C.goldDeep, margin:"0 0 4px", fontWeight:600 }}>✦ Ready to upgrade your dream statement?</p>
                <p style={{ ...bs, fontSize:11, color:C.textSoft, margin:0, lineHeight:1.5 }}>Go to Journey → Goals → "Build My Dream Statement" to create a powerful, personalized version.</p>
              </div>
            )}
          </>
        )}
      </div>
      <style>{`@keyframes dotPulse{0%,100%{transform:scale(1);opacity:.45}50%{transform:scale(1.55);opacity:1}}`}</style>
    </div>
  );
}


// ─────────────────────────────────────────────
// I AM COMPLETION SCREEN
// ─────────────────────────────────────────────
function CompletionScreen({ count, onClose }) {
  const [visible, setVisible] = useState(false);
  const message = COMPLETION_MESSAGES[Math.floor(Math.random() * COMPLETION_MESSAGES.length)];

  useEffect(() => { setTimeout(() => setVisible(true), 100); }, []);

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:300,
      background:`linear-gradient(160deg, ${C.green}, #1a2e1c, #0d1a10)`,
      display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      padding:"40px 28px",
      opacity: visible ? 1 : 0,
      transition:"opacity 0.6s ease",
    }}>
      {/* Glow */}
      <div style={{ position:"absolute", width:360, height:360, borderRadius:"50%", background:`radial-gradient(circle, rgba(201,168,76,0.15) 0%, transparent 70%)`, pointerEvents:"none" }}/>

      {/* Logo */}
      <div style={{ marginBottom:24, animation:"floatIn 0.8s ease" }}>
        <LogoMark size={72} color={C.gold}/>
      </div>

      {/* Count */}
      <div style={{ background:`rgba(201,168,76,0.12)`, border:`1px solid rgba(201,168,76,0.25)`, borderRadius:20, padding:"8px 20px", marginBottom:24 }}>
        <p style={{ ...bs, fontSize:13, color:C.gold, margin:0, letterSpacing:"0.15em", textTransform:"uppercase", fontWeight:700 }}>
          {count} Declaration{count!==1?"s":""}  Complete ✦
        </p>
      </div>

      {/* Gold divider */}
      <div style={{ width:50, height:1, background:GRAD.gold, marginBottom:28, borderRadius:1 }}/>

      {/* Message */}
      <p style={{ ...ds, fontSize:21, lineHeight:1.75, color:"rgba(255,255,255,0.93)", margin:"0 0 40px", textAlign:"center", fontStyle:"italic" }}>
        "{message}"
      </p>

      {/* Button */}
      <Btn onClick={onClose} variant="gold" style={{ maxWidth:320 }}>
        ✦ I Claim It
      </Btn>

      <style>{`
        @keyframes floatIn { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
      `}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// I AM TAB
// ─────────────────────────────────────────────
function IAmTab({ isPremium, onUpgrade }) {
  const [myList, setMyList] = useLocalStorage("iHM_iam", []);
  const [screen, setScreen] = useState("home"); // home | library | declare
  const [showShare, setShowShare] = useState(false);
  const [libCat, setLibCat] = useState("Spiritual");
  const [customText, setCustomText] = useState("");
  const [declareMode, setDeclareMode] = useState(null); // null | "manual" | "auto"
  const [declareIdx, setDeclareIdx] = useState(0);
  const [autoSpeed, setAutoSpeed] = useState(5);
  const [paused, setPaused] = useState(false);
  const intervalRef = useRef(null);


  const addStatement = (text) => {
    if (!text.trim()) return;
    if (myList.includes(text.trim())) return;
    if (!isPremium && myList.length >= 10) { setShowUpgrade(true); return; }
    setMyList(l => [...l, text.trim()]);
  };

  const removeStatement = (text) => setMyList(l => l.filter(x => x !== text));

  const startDeclare = (mode) => {
    if (myList.length === 0) return;
    setDeclareMode(mode);
    setDeclareIdx(0);
    setPaused(false);
    setScreen("declare");
  };

  // Auto advance
  useEffect(() => {
    if (screen==="declare" && declareMode==="auto" && !paused) {
      clearInterval(intervalRef.current);
      intervalRef.current = setInterval(() => {
        setDeclareIdx(i => {
          if (i >= myList.length - 1) { clearInterval(intervalRef.current); setShowCompletion(true); return i; }
          return i + 1;
        });
      }, autoSpeed * 1000);
    }
    return () => clearInterval(intervalRef.current);
  }, [screen, declareMode, paused, autoSpeed, myList.length]);

  const [showCompletion, setShowCompletion] = useState(false);

  const [showUpgrade, setShowUpgrade] = useState(false);
  const nextManual = () => {
    if (declareIdx >= myList.length - 1) {
      clearInterval(intervalRef.current);
      setShowCompletion(true);
    } else setDeclareIdx(i => i+1);
  };

  const prevManual = () => { if (declareIdx > 0) setDeclareIdx(i=>i-1); };

  const togglePause = () => {
    if (paused) { setPaused(false); }
    else { clearInterval(intervalRef.current); setPaused(true); }
  };

  // ── DECLARE SCREEN ──
  if (screen === "declare") {
    if (showCompletion) return <CompletionScreen count={myList.length} onClose={()=>{ setShowCompletion(false); setScreen("home"); setDeclareIdx(0); }}/>;
    const stmt = myList[declareIdx];
    const progress = ((declareIdx + 1) / myList.length) * 100;
    return (
      <div style={{ minHeight:"100vh", background:"linear-gradient(160deg,#1a2d20,#2d3520,#1a2535)", display:"flex", flexDirection:"column", padding:"0 24px 120px" }}>
        {/* Header */}
        <div style={{ paddingTop:52, display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:32 }}>
          <div>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.2em", textTransform:"uppercase", color:"rgba(255,255,255,0.4)", margin:"0 0 2px" }}>Declaration {declareIdx+1} of {myList.length}</p>
            <p style={{ ...bs, fontSize:12, color:"rgba(255,255,255,0.3)", margin:0, fontStyle:"italic" }}>{declareMode==="auto" ? `Auto · ${autoSpeed}s` : "Manual"}</p>
          </div>
          <button onClick={()=>{ clearInterval(intervalRef.current); setScreen("home"); setDeclareIdx(0); }} style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:12, padding:"8px 14px", color:"rgba(255,255,255,0.6)", ...bs, fontSize:13, cursor:"pointer" }}>✕ End</button>
        </div>

        {/* Progress bar */}
        <div style={{ height:3, background:"rgba(255,255,255,0.1)", borderRadius:2, marginBottom:48, overflow:"hidden" }}>
          <div style={{ height:"100%", background:"linear-gradient(90deg,#6aab7a,#c8a254)", width:`${progress}%`, borderRadius:2, transition:"width .4s" }} />
        </div>

        {/* Statement */}
        <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center" }}>
          <p style={{ ...bs, fontSize:13, letterSpacing:"0.2em", textTransform:"uppercase", color:"rgba(255,255,255,0.35)", margin:"0 0 24px" }}>SPEAK THIS OUT LOUD</p>
          <h1 style={{ ...ds, fontSize:32, lineHeight:1.5, color:"rgba(255,255,255,0.95)", margin:0, textAlign:"center", fontStyle:"italic", animation:"fadeInUp .5s ease" }}>
            {stmt}
          </h1>
        </div>

        {/* Controls */}
        <div style={{ marginTop:48 }}>
          {declareMode === "manual" ? (
            <div style={{ display:"flex", gap:12, alignItems:"center", justifyContent:"center" }}>
              <button onClick={prevManual} disabled={declareIdx===0} style={{ width:52, height:52, borderRadius:"50%", border:"1.5px solid rgba(255,255,255,0.2)", background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.6)", fontSize:20, cursor:declareIdx===0?"default":"pointer", opacity:declareIdx===0?0.3:1 }}>←</button>
              <button onClick={nextManual} style={{ flex:1, border:"2px solid rgba(255,255,255,0.3)", borderRadius:50, padding:"16px", background:"rgba(255,255,255,0.08)", color:"white", ...bs, fontSize:16, fontWeight:600, cursor:"pointer", backdropFilter:"blur(8px)" }}>
                {declareIdx >= myList.length-1 ? "✦ Complete" : "I Declare This →"}
              </button>
            </div>
          ) : (
            <button onClick={togglePause} style={{ width:"100%", border:"2px solid rgba(255,255,255,0.3)", borderRadius:50, padding:"16px", background:"rgba(255,255,255,0.08)", color:"white", ...bs, fontSize:16, fontWeight:600, cursor:"pointer" }}>
              {paused ? "▶ Resume" : "⏸ Pause"}
            </button>
          )}
        </div>
        <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
      </div>
    );
  }

  // ── LIBRARY SCREEN ──
  if (screen === "library") {
    const libStatements = IAM_LIBRARY[libCat] || [];
    return (
      <div style={{ padding:"0 20px 100px", background:GRAD.bg, minHeight:"100vh" }}>
        <div style={{ paddingTop:56, display:"flex", alignItems:"center", gap:12, marginBottom:20 }}>
          <button onClick={()=>setScreen("home")} style={{ background:"rgba(74,124,89,0.08)", border:"1px solid rgba(74,124,89,0.2)", borderRadius:12, padding:"8px 14px", ...bs, fontSize:13, color:"#4a7c59", fontWeight:600, cursor:"pointer", flexShrink:0 }}>← Back</button>
          <h2 style={{ ...ds, fontSize:22, color:C.green, margin:0 }}>Add "I Am" Declarations</h2>
        </div>

        {/* Category tabs */}
        <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:8, marginBottom:20 }}>
          {IAM_CATEGORIES.map(c => {
            const m = CAT_META[c]; const on = libCat===c;
            return <button key={c} onClick={()=>setLibCat(c)} style={{ flexShrink:0, border:on?`1.5px solid ${m.color}`:"1.5px solid rgba(0,0,0,0.1)", borderRadius:20, padding:"7px 14px", background:on?m.light:"rgba(255,255,255,0.75)", cursor:"pointer", ...bs, fontSize:12, fontWeight:600, color:on?m.color:"#6a7a65", whiteSpace:"nowrap" }}><span style={{ width:6, height:6, borderRadius:"50%", background:m.color, display:"inline-block", marginRight:7, verticalAlign:"1px", flexShrink:0 }}/>{c}</button>;
          })}
        </div>

        {/* Statements list */}
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:28 }}>
          {libStatements.map(stmt => {
            const added = myList.includes(stmt);
            return (
              <div key={stmt} style={{ background:"rgba(255,255,255,0.82)", borderRadius:16, padding:"14px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, border:"1px solid rgba(74,124,89,0.1)", boxShadow:"0 2px 8px rgba(45,74,53,0.06)" }}>
                <p style={{ ...ds, fontSize:15, color:C.green, margin:0, fontStyle:"italic", flex:1, lineHeight:1.5 }}>{stmt}</p>
                <button onClick={()=>added?removeStatement(stmt):addStatement(stmt)} style={{ flexShrink:0, border:added?`1.5px solid #c87878`:`1.5px solid #4a7c59`, borderRadius:20, padding:"6px 14px", background:added?"rgba(200,120,120,0.08)":"rgba(74,124,89,0.08)", color:added?"#c87878":"#4a7c59", cursor:"pointer", ...bs, fontSize:12, fontWeight:600, whiteSpace:"nowrap" }}>
                  {added ? "✓ Added" : "+ Add"}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── HOME SCREEN ──
  return (
    <div style={{ padding:"0 20px 100px", background:GRAD.bg, minHeight:"100vh" }}>
      {showUpgrade && <UpgradeModal featureName="Unlimited I Am Declarations" onClose={()=>setShowUpgrade(false)} onUpgrade={onUpgrade}/>}
      <div style={{ paddingTop:56 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.22em", color:C.textSoft, textTransform:"uppercase", margin:"0 0 4px" }}>Identity Declaration</p>
            <h1 style={{ ...ds, fontSize:28, color:C.green, margin:"0 0 6px", lineHeight:1.15 }}>I Am ✦</h1>
            <p style={{ ...bs, fontSize:14, color:C.textSoft, fontStyle:"italic", margin:"0 0 28px" }}>Speak your identity into existence</p>
          </div>
          <button onClick={()=>{ if(myList.length===0){ alert("Add some I Am declarations first, then share them!"); return; } setShowShare(true); }} style={{ marginTop:4, flexShrink:0, width:44, height:44, borderRadius:14, border:`1.5px solid ${C.gold}`, background:`rgba(201,168,76,0.08)`, cursor:"pointer", fontSize:16, color:C.gold, display:"flex", alignItems:"center", justifyContent:"center" }}><Icon name="share" size={18}/></button>
        </div>
        {showShare && <ShareModal profile={{dream:""}} defaultType="iam" onClose={()=>setShowShare(false)}/>}
      </div>

      {/* Write your own */}
      <div style={{ background:"rgba(255,255,255,0.82)", borderRadius:20, padding:"20px", marginBottom:16, border:`1px solid ${C.border}` }}>
        <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>Write Your Own</p>
        <div style={{ display:"flex", gap:8 }}>
          <div style={{ flex:1, position:"relative" }}>
            <span style={{ position:"absolute", left:14, top:"50%", transform:"translateY(-50%)", ...ds, fontSize:15, color:"#4a7c59", fontStyle:"italic", pointerEvents:"none" }}>I Am </span>
            <input value={customText} onChange={e=>setCustomText(e.target.value)}
              onKeyDown={e=>{ if(e.key==="Enter"&&customText.trim()){ addStatement("I Am "+customText.trim()); setCustomText(""); } }}
              placeholder="unstoppable..."
              style={{ width:"100%", boxSizing:"border-box", border:"1.5px solid rgba(74,124,89,0.25)", borderRadius:14, padding:"12px 14px 12px 48px", ...bs, fontSize:15, background:"rgba(255,255,255,0.88)", color:C.green, outline:"none" }} />
          </div>
          <button onClick={()=>{ if(customText.trim()){ addStatement("I Am "+customText.trim()); setCustomText(""); } }} style={{ flexShrink:0, border:"none", borderRadius:14, padding:"12px 16px", background:"linear-gradient(135deg,#4a7c59,#6aab7a)", color:"white", cursor:"pointer", fontSize:18 }}>+</button>
        </div>
      </div>

      {/* Add from library */}
      <button onClick={()=>setScreen("library")} style={{ width:"100%", border:"1.5px solid rgba(74,124,89,0.25)", borderRadius:20, padding:"16px 20px", background:"rgba(255,255,255,0.6)", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:24 }}>
        <div style={{ textAlign:"left" }}>
          <p style={{ ...bs, fontSize:14, fontWeight:600, color:C.green, margin:"0 0 2px", display:"flex", alignItems:"center", gap:8 }}><Icon name="doc" size={15} color={C.greenMid}/>Browse Declaration Library</p>
          <p style={{ ...bs, fontSize:12, color:C.textSoft, margin:0, fontStyle:"italic" }}>60+ "I Am" statements by category</p>
        </div>
        <span style={{ color:"#4a7c59", fontSize:18 }}>→</span>
      </button>

      {/* My list */}
      {myList.length > 0 && (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:0 }}>My Declarations ({myList.length})</p>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:24 }}>
            {myList.map((stmt,i) => (
              <div key={i} style={{ background:"rgba(255,255,255,0.82)", borderRadius:16, padding:"14px 16px", display:"flex", justifyContent:"space-between", alignItems:"center", gap:12, border:"1px solid rgba(74,124,89,0.1)", boxShadow:"0 2px 8px rgba(45,74,53,0.06)", animation:"fadeIn .3s ease" }}>
                <p style={{ ...ds, fontSize:15, color:C.green, margin:0, fontStyle:"italic", flex:1, lineHeight:1.5 }}>{stmt}</p>
                <button onClick={()=>removeStatement(stmt)} style={{ flexShrink:0, background:"none", border:"none", color:"#c0a880", cursor:"pointer", fontSize:18, padding:0 }}>×</button>
              </div>
            ))}
          </div>

          {/* Declare mode picker */}
          <div style={{ background:"rgba(255,255,255,0.75)", borderRadius:20, padding:"20px", border:`1px solid ${C.border}`, marginBottom:12 }}>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 14px" }}>Declaration Mode</p>
            <div style={{ display:"flex", gap:8, marginBottom:16 }}>
              <button onClick={()=>startDeclare("manual")} style={{ flex:1, border:"2px solid #4a7c59", borderRadius:14, padding:"12px 8px", background:"rgba(74,124,89,0.08)", cursor:"pointer", ...bs, fontSize:13, fontWeight:600, color:"#4a7c59" }}>
                👆 Manual<br/><span style={{ fontSize:11, fontWeight:400, color:C.textSoft }}>Tap to advance</span>
              </button>
              <button onClick={()=>startDeclare("auto")} style={{ flex:1, border:"2px solid #c8a254", borderRadius:14, padding:"12px 8px", background:"rgba(200,162,84,0.08)", cursor:"pointer", ...bs, fontSize:13, fontWeight:600, color:"#c8a254" }}>
                ⏱ Auto<br/><span style={{ fontSize:11, fontWeight:400, color:C.textSoft }}>Every {autoSpeed}s</span>
              </button>
            </div>
            {/* Speed slider */}
            <p style={{ ...bs, fontSize:12, color:C.textSoft, margin:"0 0 8px", fontStyle:"italic" }}>Auto speed: {autoSpeed} seconds per declaration</p>
            <input type="range" min={3} max={15} value={autoSpeed} onChange={e=>setAutoSpeed(Number(e.target.value))}
              style={{ width:"100%", accentColor:"#c8a254", cursor:"pointer" }} />
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <span style={{ ...bs, fontSize:10, color:"#b0a080" }}>3s (fast)</span>
              <span style={{ ...bs, fontSize:10, color:"#b0a080" }}>15s (slow)</span>
            </div>
          </div>
        </>
      )}

      {myList.length === 0 && (
        <div style={{ textAlign:"center", padding:"40px 20px", background:"rgba(255,255,255,0.5)", borderRadius:20, border:"2px dashed rgba(74,124,89,0.2)" }}>
          <div style={{ fontSize:40, marginBottom:12 }}>✦</div>
          <p style={{ ...ds, fontSize:18, color:"#4a7c59", margin:"0 0 6px" }}>Your declarations await</p>
          <p style={{ ...bs, fontSize:13, color:C.textSoft, fontStyle:"italic", margin:0 }}>Write your own or browse the library above</p>
        </div>
      )}
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// VISION BOARD TAB
// ─────────────────────────────────────────────
function VisionBoardTab({ profile, isPremium, onUpgrade }) {
  const [board, setBoard] = useLocalStorage("iHM_board",[]);
  const [showBoardUpgrade, setShowBoardUpgrade] = useState(false);
  const [view, setView] = useState("all");
  const [modal, setModal] = useState(null); // "add" | "detail" | "slideshow"
  const [detailItem, setDetailItem] = useState(null);
  const [addCat, setAddCat] = useState(CATEGORIES[0]);
  const [addCaption, setAddCaption] = useState("");
  const [addAffirmation, setAddAffirmation] = useState("");
  const [genLoading, setGenLoading] = useState(false);
  const [selectedImg, setSelectedImg] = useState(null);
  const fileRef = useRef(null);

  // Slideshow state
  const [slideIdx, setSlideIdx] = useState(0);
  const [slideMode, setSlideMode] = useState(null); // null | "manual" | "auto"
  const [slideSpeed, setSlideSpeed] = useState(5);
  const [slidePaused, setSlidePaused] = useState(false);
  const slideIntervalRef = useRef(null);

  const filtered = view==="all" ? board : board.filter(i=>i.category===view);
  const col1 = filtered.filter((_,i)=>i%2===0);
  const col2 = filtered.filter((_,i)=>i%2===1);
  const openAdd = () => { setModal("add"); setAddCat(profile.categories[0]||CATEGORIES[0]); setAddCaption(""); setAddAffirmation(""); setSelectedImg(null); };

  // Slideshow auto-advance
  useEffect(() => {
    if (modal==="slideshow" && slideMode==="auto" && !slidePaused) {
      clearInterval(slideIntervalRef.current);
      slideIntervalRef.current = setInterval(() => {
        setSlideIdx(i => {
          if (i >= board.length - 1) { clearInterval(slideIntervalRef.current); setModal(null); return 0; }
          return i + 1;
        });
      }, slideSpeed * 1000);
    }
    return () => clearInterval(slideIntervalRef.current);
  }, [modal, slideMode, slidePaused, slideSpeed, board.length]);

  const startSlideshow = (mode) => {
    if (board.length === 0) return;
    setSlideIdx(0);
    setSlideMode(mode);
    setSlidePaused(false);
    setModal("slideshow");
  };

  const nextSlide = () => {
    if (slideIdx >= board.length - 1) { clearInterval(slideIntervalRef.current); setModal(null); setSlideIdx(0); }
    else setSlideIdx(i => i + 1);
  };

  const prevSlide = () => { if (slideIdx > 0) setSlideIdx(i => i - 1); };

  const toggleSlidePause = () => {
    if (slidePaused) setSlidePaused(false);
    else { clearInterval(slideIntervalRef.current); setSlidePaused(true); }
  };

  const generateAffirmation = async (cat, caption) => {
    setGenLoading(true);
    try {
      const text = await callClaude("Write one short, powerful, personal affirmation (1-2 sentences, first person) tied to this image and intention. Return only the affirmation.", `Category: ${cat}. Caption: "${caption||cat}". Dream: "${profile.dream}".`);
      setAddAffirmation(text);
    } catch { setAddAffirmation(pickAffirmation([cat])); }
    setGenLoading(false);
  };

  const saveToBoard = (imgUrl) => {
    if (!imgUrl) return;
    if (!isPremium && board.length >= 3) { setShowBoardUpgrade(true); return; }
    setBoard(b => [{ id:Date.now(), url:imgUrl, category:addCat, caption:addCaption.trim()||addCat, affirmation:addAffirmation||pickAffirmation([addCat]) }, ...b]);
    setModal(null);
  };

  const handleUpload = (e) => {
    const file = e.target.files?.[0]; if(!file) return;
    const reader = new FileReader();
    reader.onload = ev => setSelectedImg(ev.target.result);
    reader.readAsDataURL(file);
  };


  return (
    <div style={{ background:GRAD.bg, minHeight:"100vh", paddingBottom:100 }}>
      {showBoardUpgrade && <UpgradeModal featureName="Unlimited Vision Board" onClose={()=>setShowBoardUpgrade(false)} onUpgrade={onUpgrade}/>}
      <div style={{ padding:"56px 20px 0" }}>
        <p style={{ ...bs, fontSize:11, letterSpacing:"0.22em", color:C.textSoft, textTransform:"uppercase", margin:"0 0 4px" }}>Vision Board</p>
        <h1 style={{ ...ds, fontSize:28, color:C.green, margin:"0 0 16px", lineHeight:1.15 }}>I See It.<br/>I Claim It. ✦</h1>
        <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:8, marginBottom:20 }}>
          {["all",...CATEGORIES].map(c => {
            const on=view===c; const m=c==="all"?null:CAT_META[c];
            return <button key={c} onClick={()=>setView(c)} style={{ flexShrink:0, border:on?`1.5px solid ${m?.color||"#4a7c59"}`:"1.5px solid rgba(0,0,0,0.1)", borderRadius:20, padding:"6px 14px", background:on?(m?.light||"rgba(74,124,89,0.1)"):"rgba(255,255,255,0.75)", cursor:"pointer", ...bs, fontSize:12, fontWeight:600, color:on?(m?.color||"#4a7c59"):"#6a7a65", whiteSpace:"nowrap" }}>{c==="all"?"✦ All":<><span style={{ width:6, height:6, borderRadius:"50%", background:(m?.color||"#4a7c59"), display:"inline-block", marginRight:7, verticalAlign:"1px", flexShrink:0 }}/>{c}</>}</button>;
          })}
        </div>
      </div>
      <div style={{ padding:"0 12px" }}>
        {board.length===0 ? (
          <div style={{ textAlign:"center", padding:"40px 20px", background:"rgba(255,255,255,0.65)", borderRadius:24, border:`2px dashed rgba(201,168,76,0.3)`, margin:"0 8px" }}>
            <LogoMark size={44} color={C.gold}/>
            <h3 style={{ ...ds, fontSize:20, color:C.green, margin:"14px 0 8px" }}>Your Vision Board Awaits</h3>
            <p style={{ ...bs, fontSize:13, color:C.textSoft, fontStyle:"italic", margin:"0 0 18px", lineHeight:1.6 }}>Add photos of the life you are claiming — your dream home, your freedom, your future.</p>
            <div style={{ display:"flex", flexDirection:"column", gap:7, textAlign:"left" }}>
              {["Tap '+ Add to My Vision' below","Upload from your camera roll","Add a caption and affirmation","Use Slideshow to visualize daily"].map((tip,i) => (
                <div key={i} style={{ background:`rgba(201,168,76,0.07)`, borderRadius:12, padding:"9px 14px", display:"flex", alignItems:"center", gap:9 }}>
                  <Icon name="spark" size={10} color={C.gold}/>
                  <span style={{ ...bs, fontSize:12, color:C.textMid }}>{tip}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div style={{ display:"flex", gap:10 }}>
            {[col1,col2].map((col,ci) => (
              <div key={ci} style={{ flex:1, display:"flex", flexDirection:"column", gap:10 }}>
                {col.map(item => (
                  <div key={item.id} onClick={()=>{setDetailItem(item);setModal("detail");}} style={{ borderRadius:18, overflow:"hidden", cursor:"pointer", boxShadow:"0 4px 16px rgba(45,74,53,0.10)", position:"relative", background:"white" }}>
                    <img src={item.url} alt={item.caption} style={{ width:"100%", display:"block" }} onError={e=>e.target.style.opacity="0.3"} />
                    <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(transparent,rgba(20,40,25,0.75))", padding:"24px 10px 10px" }}>
                      <span style={{ ...bs, fontSize:10, color:"rgba(255,255,255,0.7)", fontStyle:"italic", display:"block", marginBottom:2 }}>{CAT_META[item.category]?.emoji} {item.category}</span>
                      <span style={{ ...bs, fontSize:12, color:"white", fontWeight:600 }}>{item.caption}</span>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
      <div style={{ padding:"20px 20px 0", display:"flex", flexDirection:"column", gap:10 }}>
        {board.length > 0 && (
          <div style={{ background:"rgba(255,255,255,0.7)", borderRadius:20, padding:"16px 18px", border:`1px solid ${C.border}` }}>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 12px" }}>✦ Visualize Your Board</p>
            <div style={{ display:"flex", gap:8, marginBottom:12 }}>
              <button onClick={()=>startSlideshow("manual")} style={{ flex:1, border:"2px solid #4a7c59", borderRadius:14, padding:"12px 8px", background:"rgba(74,124,89,0.08)", cursor:"pointer", ...bs, fontSize:13, fontWeight:600, color:"#4a7c59" }}>
                👆 Manual<br/><span style={{ fontSize:11, fontWeight:400, color:C.textSoft }}>Tap to advance</span>
              </button>
              <button onClick={()=>startSlideshow("auto")} style={{ flex:1, border:"2px solid #c8a254", borderRadius:14, padding:"12px 8px", background:"rgba(200,162,84,0.08)", cursor:"pointer", ...bs, fontSize:13, fontWeight:600, color:"#c8a254" }}>
                ⏱ Auto<br/><span style={{ fontSize:11, fontWeight:400, color:C.textSoft }}>Every {slideSpeed}s</span>
              </button>
            </div>
            <input type="range" min={3} max={15} value={slideSpeed} onChange={e=>setSlideSpeed(Number(e.target.value))}
              style={{ width:"100%", accentColor:"#c8a254", cursor:"pointer" }} />
            <div style={{ display:"flex", justifyContent:"space-between" }}>
              <span style={{ ...bs, fontSize:10, color:"#b0a080" }}>3s (fast)</span>
              <span style={{ ...bs, fontSize:10, color:"#b0a080" }}>15s (slow)</span>
            </div>
          </div>
        )}
        {!isPremium && board.length >= 3 ? (
          <LockedBtn label="Add More Images" onUpgrade={onUpgrade} featureName="Unlimited Vision Board"/>
        ) : (
          <Btn onClick={openAdd} variant="green">+ Add to My Vision {!isPremium ? `(${3-board.length} free left)` : ""}</Btn>
        )}
      </div>

      {modal==="add" && (
        <div style={{ position:"fixed", inset:0, zIndex:200, display:"flex", flexDirection:"column", background:"rgba(20,35,25,0.7)", backdropFilter:"blur(8px)" }}>
          <div style={{ marginTop:"auto", background:C.bg, borderRadius:"24px 24px 0 0", padding:"24px 20px 48px", maxHeight:"90vh", overflowY:"auto" }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <h3 style={{ ...ds, fontSize:22, color:C.green, margin:0 }}>Add to Your Vision</h3>
              <button onClick={()=>setModal(null)} style={{ background:"rgba(74,124,89,0.1)", border:"none", borderRadius:"50%", width:32, height:32, cursor:"pointer", fontSize:18, color:"#4a7c59" }}>×</button>
            </div>

            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 8px" }}>Category</p>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:18 }}>
              {CATEGORIES.map(c=>{ const m=CAT_META[c]; const on=addCat===c; return <button key={c} onClick={()=>setAddCat(c)} style={{ border:on?`1.5px solid ${m.color}`:"1.5px solid rgba(0,0,0,0.1)", borderRadius:20, padding:"6px 12px", background:on?m.light:"rgba(255,255,255,0.8)", color:on?m.color:"#6a7a65", cursor:"pointer", ...bs, fontSize:12, fontWeight:600 }}><span style={{ width:6, height:6, borderRadius:"50%", background:m.color, display:"inline-block", marginRight:7, verticalAlign:"1px", flexShrink:0 }}/>{c}</button>; })}
            </div>

            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 8px" }}>Caption</p>
            <input value={addCaption} onChange={e=>setAddCaption(e.target.value)} placeholder={`My dream ${addCat.toLowerCase()} life...`}
              style={{ width:"100%", boxSizing:"border-box", border:"1.5px solid rgba(74,124,89,0.25)", borderRadius:14, padding:"12px 14px", ...bs, fontSize:14, background:"rgba(255,255,255,0.88)", color:C.green, outline:"none", marginBottom:18 }} />

            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 8px" }}>Affirmation <span style={{ textTransform:"none", fontWeight:400, fontStyle:"italic" }}>(optional)</span></p>
            <textarea value={addAffirmation} onChange={e=>setAddAffirmation(e.target.value)} placeholder="Write a personal affirmation for this image..." rows={2}
              style={{ width:"100%", boxSizing:"border-box", border:"1.5px solid rgba(74,124,89,0.25)", borderRadius:14, padding:"12px 14px", ...bs, fontSize:14, background:"rgba(255,255,255,0.88)", color:C.green, outline:"none", resize:"none", marginBottom:18 }} />

            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 12px" }}>Choose a Photo</p>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} style={{ display:"none" }}/>
            <button onClick={()=>fileRef.current?.click()} style={{ width:"100%", border:"2px dashed rgba(74,124,89,0.3)", borderRadius:16, padding:"28px 20px", background:"rgba(255,255,255,0.6)", cursor:"pointer", ...bs, fontSize:15, color:"#4a7c59", fontWeight:600, marginBottom:12, boxSizing:"border-box" }}>
              {selectedImg ? "✓ Photo selected!" : <span style={{ display:"inline-flex", alignItems:"center", gap:7, verticalAlign:"middle" }}><Icon name="camera" size={15}/>Tap to choose from your camera roll</span>}
            </button>
            {selectedImg && <img src={selectedImg} alt="preview" style={{ width:"100%", borderRadius:14, marginBottom:16, maxHeight:220, objectFit:"cover" }}/>}

            <Btn disabled={!selectedImg} onClick={()=>saveToBoard(selectedImg)} variant="green">✦ Add to My Vision Board</Btn>
          </div>
        </div>
      )}

      {/* SLIDESHOW MODAL */}
      {modal==="slideshow" && board.length > 0 && (() => {
        const item = board[slideIdx];
        const progress = ((slideIdx + 1) / board.length) * 100;
        const m = CAT_META[item.category];
        return (
          <div style={{ position:"fixed", inset:0, zIndex:200, background:"#0d1a10", display:"flex", flexDirection:"column" }}>
            {/* Header */}
            <div style={{ position:"absolute", top:0, left:0, right:0, zIndex:10, padding:"52px 20px 16px", background:"linear-gradient(rgba(0,0,0,0.6),transparent)", display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
              <div>
                <p style={{ ...bs, fontSize:11, letterSpacing:"0.2em", textTransform:"uppercase", color:"rgba(255,255,255,0.5)", margin:"0 0 2px" }}>{slideIdx+1} of {board.length}</p>
                <p style={{ ...bs, fontSize:12, color:"rgba(255,255,255,0.4)", margin:0, fontStyle:"italic" }}>{slideMode==="auto"?`Auto · ${slideSpeed}s`:"Manual"}</p>
              </div>
              <button onClick={()=>{ clearInterval(slideIntervalRef.current); setModal(null); setSlideIdx(0); }} style={{ background:"rgba(0,0,0,0.4)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:12, padding:"8px 14px", color:"rgba(255,255,255,0.7)", ...bs, fontSize:13, cursor:"pointer" }}>✕ End</button>
            </div>

            {/* Progress bar */}
            <div style={{ position:"absolute", top:0, left:0, right:0, height:3, background:"rgba(255,255,255,0.1)", zIndex:11 }}>
              <div style={{ height:"100%", background:"linear-gradient(90deg,#6aab7a,#c8a254)", width:`${progress}%`, transition:"width .5s ease" }}/>
            </div>

            {/* Full screen image */}
            <img src={item.url} alt={item.caption} key={slideIdx}
              style={{ width:"100%", height:"100%", objectFit:"cover", position:"absolute", inset:0, animation:"kenburns 9s ease-out forwards" }}/>

            {/* Bottom overlay */}
            <div style={{ position:"absolute", bottom:0, left:0, right:0, background:"linear-gradient(transparent,rgba(0,0,0,0.85))", padding:"60px 24px 130px" }}>
              <span style={{ ...bs, fontSize:12, color:m?.color||"rgba(255,255,255,0.6)", fontWeight:600, marginBottom:8, display:"block" }}><span style={{ width:7, height:7, borderRadius:"50%", background:m?.color||"rgba(255,255,255,0.6)", display:"inline-block", marginRight:7, verticalAlign:"0.5px" }}/>{item.category}</span>
              <h2 style={{ ...ds, fontSize:26, color:"white", margin:"0 0 12px", lineHeight:1.3, fontStyle:"italic" }}>{item.caption}</h2>
              {item.affirmation && (
                <p style={{ ...bs, fontSize:14, color:"rgba(255,255,255,0.7)", margin:0, lineHeight:1.6, fontStyle:"italic" }}>"{item.affirmation}"</p>
              )}
            </div>

            {/* Controls */}
            <div style={{ position:"absolute", bottom:0, left:0, right:0, padding:"0 24px 48px", display:"flex", gap:12, alignItems:"center" }}>
              {slideMode==="manual" ? (
                <>
                  <button onClick={prevSlide} disabled={slideIdx===0} style={{ width:52, height:52, borderRadius:"50%", border:"1.5px solid rgba(255,255,255,0.25)", background:"rgba(0,0,0,0.4)", color:"rgba(255,255,255,0.7)", fontSize:20, cursor:slideIdx===0?"default":"pointer", opacity:slideIdx===0?0.3:1, flexShrink:0 }}>←</button>
                  <button onClick={nextSlide} style={{ flex:1, border:"2px solid rgba(255,255,255,0.3)", borderRadius:50, padding:"16px", background:"rgba(0,0,0,0.4)", color:"white", ...bs, fontSize:15, fontWeight:600, cursor:"pointer", backdropFilter:"blur(8px)" }}>
                    {slideIdx >= board.length-1 ? "✦ Complete" : "Next →"}
                  </button>
                </>
              ) : (
                <button onClick={toggleSlidePause} style={{ width:"100%", border:"2px solid rgba(255,255,255,0.3)", borderRadius:50, padding:"16px", background:"rgba(0,0,0,0.4)", color:"white", ...bs, fontSize:16, fontWeight:600, cursor:"pointer" }}>
                  {slidePaused ? "▶ Resume" : "⏸ Pause"}
                </button>
              )}
            </div>
          </div>
        );
      })()}

      {modal==="detail" && detailItem && (
        <div style={{ position:"fixed", inset:0, zIndex:200, background:"rgba(15,25,18,0.92)", backdropFilter:"blur(10px)", display:"flex", flexDirection:"column", overflowY:"auto" }}>
          <div style={{ padding:"20px 20px 0", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <span style={{ ...bs, fontSize:12, color:"rgba(255,255,255,0.5)" }}>{CAT_META[detailItem.category]?.emoji} {detailItem.category}</span>
            <button onClick={()=>setModal(null)} style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:"50%", width:34, height:34, cursor:"pointer", fontSize:20, color:"white" }}>×</button>
          </div>
          <img src={detailItem.url} alt={detailItem.caption} style={{ width:"100%", maxHeight:"45vh", objectFit:"cover", margin:"16px 0" }}/>
          <div style={{ padding:"0 24px 48px" }}>
            <h2 style={{ ...ds, fontSize:24, color:"white", margin:"0 0 12px" }}>{detailItem.caption}</h2>
            {detailItem.affirmation && (
              <div style={{ background:"rgba(255,255,255,0.08)", borderRadius:18, padding:"18px 20px", marginBottom:24, border:"1px solid rgba(255,255,255,0.1)" }}>
                <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:"rgba(255,255,255,0.4)", margin:"0 0 8px" }}>My Affirmation</p>
                <p style={{ ...ds, fontSize:17, color:"rgba(255,255,255,0.88)", margin:0, fontStyle:"italic", lineHeight:1.7 }}>"{detailItem.affirmation}"</p>
              </div>
            )}
            <button onClick={()=>{ setBoard(b=>b.filter(x=>x.id!==detailItem.id)); setModal(null); }} style={{ width:"100%", border:"1px solid rgba(255,80,80,0.3)", borderRadius:50, padding:"13px", background:"rgba(255,80,80,0.08)", color:"rgba(255,130,130,0.8)", ...bs, fontSize:14, cursor:"pointer" }}>
              Remove from Board
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


// ─────────────────────────────────────────────
// DREAM BUILDER INLINE (used in Journey tab)
// ─────────────────────────────────────────────
function DreamBuilderInline({ name, onNext }) {
  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [currentText, setCurrentText] = useState("");
  const [building, setBuilding] = useState(false);
  const [dreamStatement, setDreamStatement] = useState("");
  const [rawAnswers, setRawAnswers] = useState({});
  const [showReveal, setShowReveal] = useState(false);
  const [editing, setEditing] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const q = DREAM_QUESTIONS[qIdx];
  const isLast = qIdx === DREAM_QUESTIONS.length - 1;

  const toggleChip = chip => setCurrentText(prev =>
    prev.includes(chip) ? prev.replace(chip,"").replace(/,\s*,/g,",").replace(/^,\s*/,"").replace(/,\s*$/,"").trim() : prev ? prev+", "+chip : chip
  );

  const buildDream = async (updated) => {
    const entries = DREAM_QUESTIONS.map(dq => `${dq.question}: ${updated[dq.id]||"Not specified"}`).join("\n");
    return await callClaude(
      "You are a powerful manifestation coach. Weave the user's answers into one rich, vivid, emotionally stirring dream statement written entirely in first person (I am, I have, I live, I feel). Make it flow like poetry in prose — specific, personal, present-tense, deeply felt. 150-200 words. One beautiful flowing paragraph. No bullet points.",
      `My name is ${name}. Here are my dream life answers:\n\n${entries}\n\nWrite my complete dream life statement as one powerful flowing paragraph.`
    );
  };

  const saveAndNext = async () => {
    const updated = { ...answers, [q.id]: currentText.trim() };
    setAnswers(updated);
    if (isLast) {
      setBuilding(true);
      try {
        const dream = await buildDream(updated);
        setDreamStatement(dream || Object.values(updated).filter(Boolean).join(". "));
        setRawAnswers(updated); setShowReveal(true);
      } catch { setDreamStatement(Object.values(updated).filter(Boolean).join(". ")); setRawAnswers(updated); setShowReveal(true); }
      setBuilding(false);
    } else { setQIdx(i=>i+1); setCurrentText(""); }
  };

  const regenerate = async () => {
    setRegenerating(true); setEditing(false);
    try { setDreamStatement(await buildDream(rawAnswers)); } catch {}
    setRegenerating(false);
  };

  if (building) return (
    <div style={{ textAlign:"center", padding:"60px 0" }}>
      <div style={{ width:44, height:44, margin:"0 auto 20px", borderRadius:"50%", border:"3px solid rgba(201,168,76,0.2)", borderTopColor:C.gold, animation:"spin 0.9s linear infinite" }}/>
      <h2 style={{ ...ds, fontSize:24, color:C.green, margin:"0 0 10px" }}>Weaving your dream...</h2>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  if (showReveal) return (
    <div style={{ animation:"fadeInUp .6s ease" }}>
      <div style={{ textAlign:"center", marginBottom:24 }}>
        <div style={{ display:"flex", justifyContent:"center", marginBottom:14 }}><Icon name="spark" size={38} color={C.gold}/></div>
        <h2 style={{ ...ds, fontSize:24, color:C.green, margin:"0 0 6px" }}>Your Dream Life</h2>
        <p style={{ ...bs, fontSize:14, color:C.textSoft, fontStyle:"italic", margin:0 }}>{name}, this is the life you are calling in</p>
      </div>
      <div style={{ background:"rgba(255,255,255,0.88)", borderRadius:24, padding:"28px 24px", border:`1px solid ${C.border}`, boxShadow:C.shadowLg, marginBottom:16, position:"relative", overflow:"hidden" }}>
        <div style={{ position:"absolute", top:0, left:"8%", right:"8%", height:3, background:GRAD.gold, borderRadius:"0 0 4px 4px" }}/>
        {regenerating ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ display:"flex", justifyContent:"center", gap:8, marginBottom:12 }}>
              {[0,1,2].map(i=><div key={i} style={{ width:8, height:8, borderRadius:"50%", background:C.greenMid, animation:`dotPulse 1.3s ease-in-out ${i*0.22}s infinite` }}/>)}
            </div>
          </div>
        ) : editing ? (
          <textarea value={dreamStatement} onChange={e=>setDreamStatement(e.target.value)} rows={9}
            style={{ width:"100%", boxSizing:"border-box", border:`1.5px solid ${C.border}`, borderRadius:14, padding:"14px", ...ds, fontSize:15, lineHeight:1.8, color:C.green, background:"rgba(255,255,255,0.7)", outline:"none", resize:"none", fontStyle:"italic" }}/>
        ) : (
          <p style={{ ...ds, fontSize:16, lineHeight:1.88, color:C.green, margin:0, fontStyle:"italic" }}>"{dreamStatement}"</p>
        )}
      </div>
      <div style={{ display:"flex", gap:8, marginBottom:12 }}>
        <button onClick={()=>setEditing(e=>!e)} style={{ flex:1, border:`1.5px solid ${C.border}`, borderRadius:50, padding:"12px", ...bs, fontSize:14, fontWeight:600, color:C.greenMid, background:`rgba(44,74,46,0.07)`, cursor:"pointer" }}>{editing?"✓ Done":"✏ Edit"}</button>
        <button onClick={regenerate} disabled={regenerating} style={{ flex:1, border:`1.5px solid rgba(155,127,200,0.35)`, borderRadius:50, padding:"12px", ...bs, fontSize:14, fontWeight:600, color:"#9b7fc8", background:"rgba(155,127,200,0.07)", cursor:regenerating?"default":"pointer", opacity:regenerating?0.5:1, display:"flex", alignItems:"center", justifyContent:"center", gap:7 }}><Icon name="refresh" size={13}/>Regenerate</button>
      </div>
      <Btn onClick={()=>onNext(dreamStatement)} variant="warm" disabled={!dreamStatement.trim()||regenerating}>✦ This is my dream — I claim it</Btn>
      <style>{`@keyframes fadeInUp{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}@keyframes dotPulse{0%,100%{transform:scale(1);opacity:.45}50%{transform:scale(1.55);opacity:1}}`}</style>
    </div>
  );

  return (
    <div>
      <ProgressBar current={qIdx+1} total={DREAM_QUESTIONS.length}/>
      <div style={{ marginBottom:20 }}>
        <div style={{ fontSize:32, marginBottom:8 }}>{q.emoji}</div>
        <h2 style={{ ...ds, fontSize:22, color:C.green, margin:"0 0 6px", lineHeight:1.25 }}>{q.question}</h2>
        <p style={{ ...bs, color:C.textSoft, fontSize:13, fontStyle:"italic", margin:0, lineHeight:1.5 }}>{q.subtitle}</p>
      </div>
      <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:14 }}>
        {q.chips.map(chip=>{ const on=currentText.includes(chip); return <button key={chip} onClick={()=>toggleChip(chip)} style={{ border:on?`1.5px solid ${C.greenMid}`:`1.5px solid ${C.border}`, borderRadius:20, padding:"7px 14px", background:on?`rgba(74,124,89,0.1)`:"rgba(255,255,255,0.75)", color:on?C.greenMid:C.textMid, cursor:"pointer", ...bs, fontSize:12, fontWeight:on?600:400 }}>{on?"✓ ":""}{chip}</button>; })}
      </div>
      <textarea value={currentText} onChange={e=>setCurrentText(e.target.value)} placeholder={q.placeholder} rows={3}
        style={{ width:"100%", boxSizing:"border-box", border:`1.5px solid ${C.border}`, borderRadius:16, padding:"12px 14px", ...bs, fontSize:14, lineHeight:1.7, background:"rgba(255,255,255,0.88)", color:C.green, outline:"none", resize:"none", marginBottom:14 }}/>
      <div style={{ display:"flex", gap:10 }}>
        {qIdx>0 && <Btn variant="ghost" onClick={()=>{setQIdx(i=>i-1);setCurrentText(answers[DREAM_QUESTIONS[qIdx-1].id]||"");}} style={{ flex:"0 0 80px" }}>← Back</Btn>}
        <div style={{ flex:1 }}><Btn disabled={!currentText.trim()} onClick={saveAndNext} variant={isLast?"warm":"green"}>{isLast?"✦ Complete":"Next →"}</Btn></div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// JOURNEY TAB — Check-In + Gratitude + Goals
// ─────────────────────────────────────────────
function JourneyTab({ profile, onUpdateDream, isPremium, onUpgrade }) {
  const todayKey = new Date().toISOString().slice(0,10);
  const [showShareJ, setShowShareJ] = useState(false);
  const [checkIns, setCheckIns] = useLocalStorage("iHM_checkins", {});
  const [gratitude, setGratitude] = useLocalStorage("iHM_gratitude", {});
  const [goals, setGoals] = useLocalStorage("iHM_goals", []);
  const [section, setSection] = useState("checkin"); // checkin | gratitude | goals | reminders
  const [newGoalText, setNewGoalText] = useState("");
  const [newGoalCat, setNewGoalCat] = useState("Wealth");
  const [showGoalForm, setShowGoalForm] = useState(false);
  const [showDreamBuilder, setShowDreamBuilder] = useState(false);
  const [gratitudeSaved, setGratitudeSaved] = useState(false);
  const [checkInSaved, setCheckInSaved] = useState(false);
  const [notifStatus, setNotifStatus] = useState("idle"); // idle | granted | denied | unsupported
  const [reminders, setReminders] = useLocalStorage("iHM_reminders", {
    morning: { on:true,  hour:7,  minute:0,  label:"Morning Affirmation",  message:"Your daily affirmation is ready. Speak it out loud." },
    midday:  { on:true,  hour:12, minute:0,  label:"Midday Declaration",   message:"Speak your truth. Tap to declare your I Am." },
    evening: { on:true,  hour:21, minute:0,  label:"Evening Gratitude",    message:"Time to give thanks. Log your gratitude for today." },
  });

  // On mount: reflect real permission state (so returning users don't see "Enable" after already granting),
  // and make sure the native schedule matches whatever's stored — covers the case where reminders were
  // edited on a previous, un-synced version of the app.
  useEffect(() => {
    (async () => {
      const status = await checkNotifPermission();
      setNotifStatus(status);
      if (status === "granted") await syncAllReminders(reminders);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const todayCheckIn = checkIns[todayKey] || { feeling: null, intention: "", word: "" };
  const todayGratitude = gratitude[todayKey] || ["", "", ""];

  const CHECKIN_FEELINGS = [
    { emoji:"🔥", label:"On Fire" },
    { emoji:"😊", label:"Good" },
    { emoji:"😌", label:"Peaceful" },
    { emoji:"😐", label:"Neutral" },
    { emoji:"😔", label:"Low" },
  ];

  const FOCUS_WORDS = ["Abundance","Faith","Focus","Gratitude","Healing","Joy","Love","Peace","Power","Purpose","Strength","Trust"];

  const saveCheckIn = (updates) => {
    setCheckIns(c => ({ ...c, [todayKey]: { ...todayCheckIn, ...updates } }));
  };

  const saveGratitude = (idx, val) => {
    const updated = [...todayGratitude];
    updated[idx] = val;
    setGratitude(g => ({ ...g, [todayKey]: updated }));
  };

  const addGoal = () => {
    if (!newGoalText.trim()) return;
    setGoals(g => [...g, { id: Date.now(), text: newGoalText.trim(), category: newGoalCat, progress: 0, done: false }]);
    setNewGoalText(""); setShowGoalForm(false);
  };

  const updateProgress = (id, progress) => setGoals(g => g.map(x => x.id===id ? {...x, progress} : x));
  const toggleDone = (id) => setGoals(g => g.map(x => x.id===id ? {...x, done:!x.done} : x));
  const removeGoal = (id) => setGoals(g => g.filter(x => x.id!==id));

  const recentDates = [...Array(5)].map((_,i) => {
    const d = new Date(); d.setDate(d.getDate()-i);
    return d.toISOString().slice(0,10);
  });

  return (
    <div style={{ background:GRAD.bg, minHeight:"100vh", paddingBottom:100 }}>
      {showDreamBuilder && (
        <div style={{ position:"fixed", inset:0, zIndex:400, background:GRAD.bg, overflowY:"auto" }}>
          <div style={{ padding:"52px 24px 60px" }}>
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:28 }}>
              <button onClick={()=>setShowDreamBuilder(false)} style={{ background:`rgba(44,74,46,0.08)`, border:`1px solid ${C.border}`, borderRadius:12, padding:"8px 14px", ...bs, fontSize:13, color:C.green, fontWeight:600, cursor:"pointer" }}>← Back</button>
              <h2 style={{ ...ds, fontSize:22, color:C.green, margin:0 }}>Build My Dream ✦</h2>
            </div>
            <DreamBuilderInline name={profile.name} onNext={dream=>{ onUpdateDream(dream); setShowDreamBuilder(false); }}/>
          </div>
        </div>
      )}
      {/* Header */}
      <div style={{ padding:"56px 20px 0" }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start" }}>
          <div>
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.22em", color:C.textSoft, textTransform:"uppercase", margin:"0 0 4px" }}>My Journey</p>
            <h1 style={{ ...ds, fontSize:28, color:C.green, margin:"0 0 20px", lineHeight:1.15 }}>Living My Dream<br/>Every Day ✦</h1>
          </div>
          <button onClick={()=>setShowShareJ(true)} style={{ marginTop:4, flexShrink:0, width:44, height:44, borderRadius:14, border:`1.5px solid ${C.gold}`, background:`rgba(201,168,76,0.08)`, cursor:"pointer", fontSize:16, color:C.gold, display:"flex", alignItems:"center", justifyContent:"center" }}><Icon name="share" size={18}/></button>
        </div>
        {showShareJ && <ShareModal profile={profile} defaultType="dream" onClose={()=>setShowShareJ(false)}/>}

        {/* Section tabs */}
        <div style={{ display:"flex", gap:6, marginBottom:28 }}>
          {[{id:"checkin",icon:"sun",label:"Check-In"},{id:"gratitude",icon:"heart",label:"Gratitude"},{id:"goals",icon:"target",label:"Goals"},{id:"reminders",icon:"bell",label:"Remind"}].map(s => (
            <button key={s.id} onClick={()=>setSection(s.id)} style={{ flex:1, border:section===s.id?`2px solid ${C.gold}`:`1.5px solid ${C.border}`, borderRadius:14, padding:"9px 4px 8px", background:section===s.id?`rgba(201,168,76,0.1)`:"rgba(255,255,255,0.75)", cursor:"pointer", ...bs, fontSize:10, fontWeight:600, color:section===s.id?C.goldDeep:C.textMid, transition:"all .15s", display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}><Icon name={s.icon} size={14} color={section===s.id?C.goldDeep:C.textSoft}/><span>{s.label}</span></button>
          ))}
        </div>
      </div>

      <div style={{ padding:"0 20px" }}>

        {/* ── CHECK-IN SECTION ── */}
        {section==="checkin" && (
          <div style={{ animation:"fadeIn .3s ease" }}>
            <div style={{ background:"rgba(255,255,255,0.82)", borderRadius:24, padding:"24px", marginBottom:16, border:"1px solid rgba(74,124,89,0.12)", boxShadow:"0 4px 20px rgba(45,74,53,0.07)" }}>
              <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 14px" }}>How are you feeling today?</p>
              <div style={{ display:"flex", gap:8, marginBottom:20 }}>
                {CHECKIN_FEELINGS.map(f => {
                  const on = todayCheckIn.feeling === f.label;
                  return (
                    <button key={f.label} onClick={()=>saveCheckIn({feeling:f.label})} style={{ flex:1, border:on?"2px solid #4a7c59":"1.5px solid rgba(0,0,0,0.08)", borderRadius:14, padding:"10px 4px", background:on?"rgba(74,124,89,0.1)":"rgba(255,255,255,0.8)", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                      <span style={{ fontSize:22 }}>{f.emoji}</span>
                      <span style={{ ...bs, fontSize:9, color:on?"#4a7c59":"#8a9a80", fontWeight:on?700:400, letterSpacing:"0.05em" }}>{f.label.toUpperCase()}</span>
                    </button>
                  );
                })}
              </div>

              <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>Today's Intention</p>
              <input value={todayCheckIn.intention} onChange={e=>saveCheckIn({intention:e.target.value})}
                placeholder="I intend to show up today by..."
                style={{ width:"100%", boxSizing:"border-box", border:"1.5px solid rgba(74,124,89,0.22)", borderRadius:14, padding:"12px 14px", ...bs, fontSize:14, background:"rgba(255,255,255,0.88)", color:C.green, outline:"none", marginBottom:16 }}/>

              <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>My Focus Word</p>
              <div style={{ display:"flex", flexWrap:"wrap", gap:7 }}>
                {FOCUS_WORDS.map(w => {
                  const on = todayCheckIn.word === w;
                  return (
                    <button key={w} onClick={()=>saveCheckIn({word:w})} style={{ border:on?"1.5px solid #4a7c59":"1.5px solid rgba(74,124,89,0.2)", borderRadius:20, padding:"6px 14px", background:on?"rgba(74,124,89,0.12)":"rgba(255,255,255,0.75)", cursor:"pointer", ...bs, fontSize:12, fontWeight:on?700:400, color:on?"#4a7c59":"#5a6a55" }}>{w}</button>
                  );
                })}
              </div>
            </div>

            {(todayCheckIn.feeling || todayCheckIn.intention || todayCheckIn.word) && (
              <button onClick={()=>{ document.activeElement.blur(); setCheckInSaved(true); setTimeout(()=>setCheckInSaved(false),2500); }}
                style={{ width:"100%", marginBottom:20, border:`2px solid ${C.greenMid}`, borderRadius:50, padding:"13px", background:checkInSaved?GRAD.green:`rgba(74,124,89,0.08)`, color:checkInSaved?"white":C.greenMid, cursor:"pointer", ...bs, fontSize:14, fontWeight:700, transition:"all .3s", letterSpacing:"0.06em" }}>
                {checkInSaved ? "✓ Check-In Saved!" : "Save My Check-In"}
              </button>
            )}
            {/* Recent check-ins */}
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>Recent Check-Ins</p>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {recentDates.filter(d => checkIns[d]?.feeling).map(d => {
                const ci = checkIns[d];
                const f = CHECKIN_FEELINGS.find(x=>x.label===ci.feeling);
                const label = d===todayKey ? "Today" : new Date(d+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"});
                return (
                  <div key={d} style={{ background:"rgba(255,255,255,0.7)", borderRadius:16, padding:"12px 16px", display:"flex", alignItems:"center", gap:12, border:"1px solid rgba(74,124,89,0.1)" }}>
                    <span style={{ fontSize:22 }}>{f?.emoji}</span>
                    <div style={{ flex:1 }}>
                      <p style={{ ...bs, fontSize:12, fontWeight:600, color:C.green, margin:"0 0 2px" }}>{label} · {ci.feeling}</p>
                      {ci.word && <p style={{ ...bs, fontSize:11, color:C.textSoft, margin:0, fontStyle:"italic" }}>Focus: {ci.word}</p>}
                    </div>
                  </div>
                );
              })}
              {recentDates.filter(d=>checkIns[d]?.feeling).length===0 && (
                <p style={{ ...bs, fontSize:13, color:"#b0a080", fontStyle:"italic", textAlign:"center", padding:"16px 0" }}>Your check-in history will appear here</p>
              )}
            </div>
          </div>
        )}

        {/* ── GRATITUDE SECTION ── */}
        {section==="gratitude" && (
          <div style={{ animation:"fadeIn .3s ease" }}>
            <div style={{ background:"rgba(255,255,255,0.82)", borderRadius:24, padding:"24px", marginBottom:20, border:"1px solid rgba(74,124,89,0.12)", boxShadow:"0 4px 20px rgba(45,74,53,0.07)" }}>
              <p style={{ ...ds, fontSize:18, color:C.green, margin:"0 0 4px" }}>Today I Am Grateful For...</p>
              <p style={{ ...bs, fontSize:13, color:C.textSoft, fontStyle:"italic", margin:"0 0 20px" }}>{new Date().toLocaleDateString("en-US",{weekday:"long",month:"long",day:"numeric"})}</p>
              {[0,1,2].map(i => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, marginBottom:12 }}>
                  <div style={{ width:28, height:28, borderRadius:"50%", background:"linear-gradient(135deg,#4a7c59,#6aab7a)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <span style={{ color:"white", fontSize:13, fontWeight:700 }}>{i+1}</span>
                  </div>
                  <input value={todayGratitude[i]} onChange={e=>saveGratitude(i,e.target.value)}
                    placeholder={["I am grateful for...","Something beautiful today was...","A person I appreciate is..."][i]}
                    style={{ flex:1, border:"1.5px solid rgba(74,124,89,0.22)", borderRadius:14, padding:"12px 14px", ...bs, fontSize:14, background:"rgba(255,255,255,0.88)", color:C.green, outline:"none" }}/>
                </div>
              ))}
              {todayGratitude.some(x=>x.trim()) && (
                <button onClick={()=>{ document.activeElement.blur(); setGratitudeSaved(true); setTimeout(()=>setGratitudeSaved(false),2500); }}
                  style={{ width:"100%", marginTop:8, border:`2px solid ${C.greenMid}`, borderRadius:50, padding:"13px", background:gratitudeSaved?GRAD.green:`rgba(74,124,89,0.08)`, color:gratitudeSaved?"white":C.greenMid, cursor:"pointer", ...bs, fontSize:14, fontWeight:700, transition:"all .3s", letterSpacing:"0.06em" }}>
                  {gratitudeSaved ? "✓ Gratitude Saved!" : "Save My Gratitude"}
                </button>
              )}
            </div>

            {/* Past gratitude log */}
            <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>Gratitude History</p>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {recentDates.filter(d => d!==todayKey && gratitude[d]?.some(x=>x)).map(d => (
                <div key={d} style={{ background:"rgba(255,255,255,0.7)", borderRadius:18, padding:"16px", border:"1px solid rgba(74,124,89,0.1)" }}>
                  <p style={{ ...bs, fontSize:11, letterSpacing:"0.12em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>
                    {new Date(d+"T12:00:00").toLocaleDateString("en-US",{weekday:"long",month:"short",day:"numeric"})}
                  </p>
                  {gratitude[d].filter(x=>x).map((item,i) => (
                    <p key={i} style={{ ...bs, fontSize:14, color:C.green, margin:"0 0 6px", lineHeight:1.5 }}>✦ {item}</p>
                  ))}
                </div>
              ))}
              {recentDates.filter(d=>d!==todayKey&&gratitude[d]?.some(x=>x)).length===0 && (
                <p style={{ ...bs, fontSize:13, color:"#b0a080", fontStyle:"italic", textAlign:"center", padding:"16px 0" }}>Your gratitude history will appear here</p>
              )}
            </div>
          </div>
        )}

        {/* ── GOALS SECTION ── */}
        {section==="goals" && (
          <div style={{ animation:"fadeIn .3s ease" }}>
            {/* Dream Statement upgrade */}
            <div style={{ background:`linear-gradient(135deg, rgba(44,74,46,0.95), rgba(28,46,30,0.98))`, borderRadius:20, padding:"18px 20px", marginBottom:16, border:`1px solid rgba(201,168,76,0.2)` }}>
              <p style={{ ...ds, fontSize:16, color:C.gold, margin:"0 0 6px" }}>✦ My Dream Life Statement</p>
              <p style={{ ...bs, fontSize:13, color:"rgba(255,255,255,0.55)", margin:"0 0 14px", lineHeight:1.5, fontStyle:"italic" }}>
                {profile.dream ? `"${profile.dream.slice(0,120)}${profile.dream.length>120?"...":""}"` : "You haven't built your dream statement yet."}
              </p>
              {isPremium ? (
                <button onClick={()=>setShowDreamBuilder(true)} style={{ width:"100%", border:`1.5px solid ${C.gold}`, borderRadius:50, padding:"11px", background:`rgba(201,168,76,0.12)`, color:C.gold, cursor:"pointer", ...bs, fontSize:13, fontWeight:700, letterSpacing:"0.08em" }}>
                  {profile.dream ? "✏ Rebuild My Dream Statement" : "✦ Build My Dream Statement"}
                </button>
              ) : (
                <LockedBtn label="Build My Dream Statement" onUpgrade={onUpgrade} featureName="Dream Builder"/>
              )}
            </div>

            {/* Add goal form */}
            {showGoalForm ? (
              <div style={{ background:"rgba(255,255,255,0.85)", borderRadius:24, padding:"20px", marginBottom:16, border:"1px solid rgba(74,124,89,0.15)" }}>
                <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 10px" }}>Category</p>
                <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:14 }}>
                  {CATEGORIES.map(c=>{ const m=CAT_META[c]; const on=newGoalCat===c; return <button key={c} onClick={()=>setNewGoalCat(c)} style={{ border:on?`1.5px solid ${m.color}`:"1.5px solid rgba(0,0,0,0.1)", borderRadius:20, padding:"5px 12px", background:on?m.light:"rgba(255,255,255,0.8)", color:on?m.color:"#6a7a65", cursor:"pointer", ...bs, fontSize:12, fontWeight:600 }}><span style={{ width:6, height:6, borderRadius:"50%", background:m.color, display:"inline-block", marginRight:7, verticalAlign:"1px", flexShrink:0 }}/>{c}</button>; })}
                </div>
                <p style={{ ...bs, fontSize:11, letterSpacing:"0.16em", textTransform:"uppercase", color:C.textSoft, margin:"0 0 8px" }}>My Goal</p>
                <input value={newGoalText} onChange={e=>setNewGoalText(e.target.value)}
                  placeholder="e.g. Save $10,000 by December..."
                  style={{ width:"100%", boxSizing:"border-box", border:"1.5px solid rgba(74,124,89,0.25)", borderRadius:14, padding:"12px 14px", ...bs, fontSize:14, background:"rgba(255,255,255,0.88)", color:C.green, outline:"none", marginBottom:14 }}/>
                <div style={{ display:"flex", gap:8 }}>
                  <Btn variant="ghost" onClick={()=>setShowGoalForm(false)} style={{ flex:"0 0 80px" }}>Cancel</Btn>
                  <div style={{ flex:1 }}><Btn disabled={!newGoalText.trim()} onClick={addGoal} variant="green">✦ Add Goal</Btn></div>
                </div>
              </div>
            ) : (
              <Btn onClick={()=>setShowGoalForm(true)} variant="green" style={{ marginBottom:20 }}>+ Add a New Goal</Btn>
            )}

            {/* Goals list */}
            {goals.length===0 && !showGoalForm && (
              <div style={{ textAlign:"center", padding:"40px 20px", background:"rgba(255,255,255,0.5)", borderRadius:20, border:"2px dashed rgba(74,124,89,0.2)" }}>
                <div style={{ display:"flex", justifyContent:"center", marginBottom:12 }}><Icon name="target" size={36} color={C.goldDeep}/></div>
                <p style={{ ...ds, fontSize:18, color:"#4a7c59", margin:"0 0 6px" }}>Set your first goal</p>
                <p style={{ ...bs, fontSize:13, color:C.textSoft, fontStyle:"italic", margin:0 }}>Break your big dream into specific, trackable goals</p>
              </div>
            )}

            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
              {goals.map(goal => {
                const m = CAT_META[goal.category];
                return (
                  <div key={goal.id} style={{ background:"rgba(255,255,255,0.82)", borderRadius:20, padding:"18px", border:`1px solid ${goal.done?"rgba(74,124,89,0.3)":"rgba(0,0,0,0.08)"}`, boxShadow:"0 2px 12px rgba(45,74,53,0.06)", opacity:goal.done?0.7:1, animation:"fadeIn .3s ease" }}>
                    <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                      <div style={{ flex:1 }}>
                        <span style={{ background:m.light, color:m.color, borderRadius:20, padding:"3px 10px", ...bs, fontSize:10, fontWeight:700, letterSpacing:"0.08em", marginBottom:6, display:"inline-block" }}><span style={{ width:5, height:5, borderRadius:"50%", background:m.color, display:"inline-block", marginRight:6, verticalAlign:"1px", flexShrink:0 }}/>{goal.category}</span>
                        <p style={{ ...ds, fontSize:16, color: goal.done?"#8a9a80":"#2d4a35", margin:0, lineHeight:1.4, fontStyle:"italic", textDecoration:goal.done?"line-through":"none" }}>{goal.text}</p>
                      </div>
                      <button onClick={()=>removeGoal(goal.id)} style={{ background:"none", border:"none", color:"#c0a880", cursor:"pointer", fontSize:18, padding:"0 0 0 8px", flexShrink:0 }}>×</button>
                    </div>

                    {/* Progress bar */}
                    <div style={{ marginBottom:8 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                        <span style={{ ...bs, fontSize:11, color:C.textSoft }}>Progress</span>
                        <span style={{ ...bs, fontSize:11, color:"#4a7c59", fontWeight:600 }}>{goal.progress}%</span>
                      </div>
                      <input type="range" min={0} max={100} value={goal.progress}
                        onChange={e=>updateProgress(goal.id,Number(e.target.value))}
                        style={{ width:"100%", accentColor:m.color, cursor:"pointer" }}/>
                    </div>

                    <button onClick={()=>toggleDone(goal.id)} style={{ width:"100%", border:`1.5px solid ${goal.done?"#c87878":"#4a7c59"}`, borderRadius:50, padding:"9px", background:goal.done?"rgba(200,120,120,0.08)":"rgba(74,124,89,0.07)", color:goal.done?"#c87878":"#4a7c59", cursor:"pointer", ...bs, fontSize:13, fontWeight:600 }}>
                      {goal.done ? "↩ Mark Incomplete" : "✓ Mark Complete"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ── REMINDERS SECTION ── */}
        {section==="reminders" && (
          <div style={{ animation:"fadeIn .3s ease" }}>

            {/* Permission banner */}
            <div style={{ background:`linear-gradient(135deg, rgba(44,74,46,0.95), rgba(28,46,30,0.98))`, borderRadius:20, padding:"20px", marginBottom:20, border:`1px solid rgba(201,168,76,0.2)` }}>
              <p style={{ ...ds, fontSize:17, color:C.gold, margin:"0 0 6px" }}>Daily Reminders ✦</p>
              <p style={{ ...bs, fontSize:13, color:"rgba(255,255,255,0.6)", margin:"0 0 16px", lineHeight:1.5, fontStyle:"italic" }}>
                Stay consistent with your practice. Set reminders to affirm, declare, and give thanks daily.
              </p>
              <button onClick={async () => {
                buzz();
                const perm = await requestNotifPermission();
                setNotifStatus(perm);
                if (perm === "granted") {
                  await syncAllReminders(reminders);
                  const LN = localNotifsPlugin();
                  if (LN) {
                    try { await LN.schedule({ notifications: [{ id: 999, title:"iHart2Manifest ✦", body:"Reminders are now active. YOUR WORDS HAVE POWER. It's Time to Use Them.", schedule:{ at: new Date(Date.now()+2000) } }] }); } catch {}
                  } else if ("Notification" in window) {
                    new Notification("iHart2Manifest ✦", {
                      body: "Reminders are now active. YOUR WORDS HAVE POWER. It's Time to Use Them.",
                      icon: "/favicon.ico",
                    });
                  }
                }
              }} style={{ width:"100%", border:`2px solid ${C.gold}`, borderRadius:50, padding:"13px", background:`rgba(201,168,76,0.12)`, color:C.gold, cursor:"pointer", ...bs, fontSize:14, fontWeight:700, letterSpacing:"0.08em" }}>
                {notifStatus==="granted" ? "✓ Notifications Enabled" :
                 notifStatus==="denied"  ? "Blocked — Enable in Settings" :
                 notifStatus==="unsupported" ? "Not Supported in This Browser" :
                 <span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}><Icon name="bell" size={15}/>Enable Notifications</span>}
              </button>
              {notifStatus==="denied" && (
                <p style={{ ...bs, fontSize:11, color:"rgba(201,168,76,0.5)", textAlign:"center", marginTop:8, fontStyle:"italic" }}>
                  Go to Settings → Safari → Notifications to enable
                </p>
              )}
              {notifStatus!=="granted" && notifStatus!=="denied" && (
                <p style={{ ...bs, fontSize:11, color:"rgba(255,255,255,0.3)", textAlign:"center", marginTop:8, fontStyle:"italic" }}>
                  Works best when added to your iPhone home screen via Safari
                </p>
              )}
            </div>

            {/* Reminder cards */}
            {[
              { key:"morning", icon:"sunrise", time:"Morning" },
              { key:"midday",  icon:"sun", time:"Midday" },
              { key:"evening", icon:"moon", time:"Evening" },
            ].map(r => {
              const rem = reminders[r.key];
              const timeStr = `${rem.hour.toString().padStart(2,"0")}:${rem.minute.toString().padStart(2,"0")}`;
              return (
                <div key={r.key} style={{ background:"rgba(255,255,255,0.82)", borderRadius:20, padding:"18px 20px", marginBottom:12, border:`1px solid ${C.border}`, boxShadow:`0 2px 12px rgba(28,46,30,0.06)`, opacity:rem.on?1:0.55, transition:"opacity .2s" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:rem.on?14:0 }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <Icon name={r.icon} size={21} color={C.goldDeep}/>
                      <div>
                        <p style={{ ...bs, fontSize:14, fontWeight:700, color:C.green, margin:"0 0 2px" }}>{rem.label}</p>
                        <p style={{ ...bs, fontSize:12, color:C.textSoft, margin:0, fontStyle:"italic" }}>{rem.message}</p>
                      </div>
                    </div>
                    {/* Toggle */}
                    <button onClick={()=>{
                      buzz();
                      const updated = { ...rem, on: !rem.on };
                      setReminders(rv=>({...rv,[r.key]:updated}));
                      if (notifStatus==="granted") syncReminder(r.key, updated);
                    }}
                      style={{ width:48, height:28, borderRadius:14, border:"none", background:rem.on?C.greenMid:"rgba(0,0,0,0.15)", cursor:"pointer", position:"relative", transition:"background .2s", flexShrink:0 }}>
                      <div style={{ width:22, height:22, borderRadius:"50%", background:"white", position:"absolute", top:3, left:rem.on?23:3, transition:"left .2s", boxShadow:"0 1px 4px rgba(0,0,0,0.2)" }}/>
                    </button>
                  </div>

                  {/* Time picker — only show when on */}
                  {rem.on && (
                    <div style={{ display:"flex", alignItems:"center", gap:10, paddingTop:12, borderTop:`1px solid ${C.border}` }}>
                      <span style={{ ...bs, fontSize:12, color:C.textSoft, flexShrink:0 }}>Remind me at:</span>
                      <input type="time" value={timeStr}
                        onChange={e => {
                          const [h,m] = e.target.value.split(":").map(Number);
                          const updated = { ...rem, hour:h, minute:m };
                          setReminders(rv=>({...rv,[r.key]:updated}));
                          if (notifStatus==="granted") syncReminder(r.key, updated);
                        }}
                        style={{ flex:1, border:`1.5px solid rgba(201,168,76,0.3)`, borderRadius:12, padding:"8px 12px", ...bs, fontSize:15, color:C.green, background:"rgba(255,255,255,0.9)", outline:"none", accentColor:C.gold }}/>
                      <span style={{ ...bs, fontSize:11, color:C.gold, fontWeight:600 }}>
                        {rem.hour < 12 ? "AM" : "PM"}
                      </span>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Enable all */}
            <div style={{ marginTop:8 }}>
              <Btn variant="green" onClick={()=>{
                const updated = Object.fromEntries(Object.entries(reminders).map(([k,v])=>[k,{...v,on:true}]));
                setReminders(updated);
                if (notifStatus==="granted") syncAllReminders(updated);
              }}>
                ✦ Enable All Reminders
              </Btn>
            </div>

            <p style={{ ...bs, fontSize:11, color:C.textSoft, textAlign:"center", marginTop:16, lineHeight:1.6, fontStyle:"italic" }}>
              {localNotifsPlugin()
                ? "Reminders are scheduled on your device and will arrive even when the app is closed."
                : "Notification times are saved automatically. For the best experience, add iHart2Manifest to your iPhone home screen via Safari."}
            </p>
          </div>
        )}

      </div>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// TAB BAR
// ─────────────────────────────────────────────
// SHARE CARD COMPONENT
// ─────────────────────────────────────────────
function ShareModal({ profile, defaultText, defaultType, onClose }) {
  const [shareType, setShareType] = useState(defaultType || "affirmation");
  const [format, setFormat] = useState("story");   // story | square
  const [style, setStyle] = useState("dark");       // dark | light
  const [customText, setCustomText] = useState(defaultText || "");
  const cardRef = useRef(null);
  const [copied, setCopied] = useState(false);

  const SHARE_TYPES = [
    { id:"affirmation", label:"Affirmation", icon:"sun" },
    { id:"iam",         label:"I Am",        icon:"spark" },
    { id:"dream",       label:"My Dream",    icon:"diamond" },
  ];

  // Pull content based on type
  useEffect(() => {
    if (shareType === "affirmation") {
      try { setCustomText(JSON.parse(localStorage.getItem("iHM_aff")) || ""); } catch {}
    } else if (shareType === "iam") {
      try {
        const list = JSON.parse(localStorage.getItem("iHM_iam")) || [];
        if (list.length > 0) {
          const random = list[Math.floor(Math.random() * list.length)];
          setCustomText(random);
        } else { setCustomText("I Am Unstoppable."); }
      } catch { setCustomText("I Am Unstoppable."); }
    } else if (shareType === "dream") {
      setCustomText(profile.dream || "");
    }
  }, [shareType]);

  const isDark = style === "dark";
  const isStory = format === "story";

  const cardW = isStory ? 280 : 300;
  const cardH = isStory ? 480 : 300;

  const cardStyle = {
    width: cardW, height: cardH,
    background: isDark ? GRAD.dark : `linear-gradient(160deg, ${C.bg} 0%, ${C.bgDeep} 100%)`,
    borderRadius: 24,
    display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center",
    padding: "28px 24px", boxSizing: "border-box",
    position: "relative", overflow: "hidden",
    boxShadow: "0 12px 48px rgba(0,0,0,0.25)",
    border: isDark ? `1px solid rgba(201,168,76,0.2)` : `1px solid rgba(44,74,46,0.15)`,
  };

  const [generating, setGenerating] = useState(false);

  const handleShare = async () => {
    setGenerating(true);
    try {
      if (cardRef.current) {
        const canvas = await html2canvas(cardRef.current, { scale:3, useCORS:true, logging:false });
        canvas.toBlob(async (blob) => {
          if (blob && navigator.share) {
            const file = new File([blob], "ihart2manifest.png", { type:"image/png" });
            try {
              await navigator.share({ files:[file], title:"iHart2Manifest", text:"YOUR WORDS HAVE POWER. It's Time to Use Them." });
              setGenerating(false); return;
            } catch {}
          }
          // Fallback: download image
          const url = canvas.toDataURL("image/png");
          const a = document.createElement("a");
          a.href = url; a.download = "ihart2manifest.png"; a.click();
          setGenerating(false);
        }, "image/png");
        return;
      }
    } catch {}
    // Text fallback
    const shareText = `"${customText}"\n\n— iHart2Manifest\nYOUR WORDS HAVE POWER. It's Time to Use Them.`;
    if (navigator.share) {
      try { await navigator.share({ text:shareText, title:"iHart2Manifest" }); setGenerating(false); return; } catch {}
    }
    try { await navigator.clipboard.writeText(shareText); setCopied(true); setTimeout(()=>setCopied(false),3000); } catch {}
    setGenerating(false);
  };

  return (
    <div style={{ position:"fixed", inset:0, zIndex:300, background:"rgba(10,20,12,0.88)", backdropFilter:"blur(12px)", display:"flex", flexDirection:"column", overflowY:"auto" }}>
      <div style={{ padding:"52px 20px 0", display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
        <h3 style={{ ...ds, fontSize:22, color:C.gold, margin:0 }}>Share Your Light ✦</h3>
        <button onClick={onClose} style={{ background:"rgba(255,255,255,0.08)", border:"1px solid rgba(255,255,255,0.15)", borderRadius:"50%", width:34, height:34, cursor:"pointer", fontSize:18, color:"rgba(255,255,255,0.7)" }}>×</button>
      </div>

      {/* Type selector */}
      <div style={{ padding:"0 20px", marginBottom:16 }}>
        <p style={{ ...bs, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(201,168,76,0.5)", margin:"0 0 10px" }}>What to Share</p>
        <div style={{ display:"flex", gap:8 }}>
          {SHARE_TYPES.map(t => {
            const on = shareType===t.id;
            return (
              <button key={t.id} onClick={()=>setShareType(t.id)} style={{ flex:1, border:on?`2px solid ${C.gold}`:"1.5px solid rgba(201,168,76,0.2)", borderRadius:14, padding:"10px 6px", background:on?"rgba(201,168,76,0.12)":"rgba(255,255,255,0.04)", cursor:"pointer", display:"flex", flexDirection:"column", alignItems:"center", gap:4 }}>
                <Icon name={t.icon} size={16} color={on?C.gold:"rgba(255,255,255,0.45)"}/>
                <span style={{ ...bs, fontSize:11, fontWeight:on?700:400, color:on?C.gold:"rgba(255,255,255,0.4)" }}>{t.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Format + Style */}
      <div style={{ padding:"0 20px", marginBottom:20, display:"flex", gap:10 }}>
        <div style={{ flex:1 }}>
          <p style={{ ...bs, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(201,168,76,0.5)", margin:"0 0 8px" }}>Format</p>
          <div style={{ display:"flex", gap:6 }}>
            {[{id:"story",label:"Story"},{id:"square",label:"Square"}].map(f => {
              const on = format===f.id;
              return <button key={f.id} onClick={()=>setFormat(f.id)} style={{ flex:1, border:on?`2px solid ${C.gold}`:"1.5px solid rgba(201,168,76,0.2)", borderRadius:10, padding:"8px 4px", background:on?"rgba(201,168,76,0.12)":"rgba(255,255,255,0.04)", cursor:"pointer", ...bs, fontSize:12, fontWeight:on?700:400, color:on?C.gold:"rgba(255,255,255,0.4)" }}>{f.label}</button>;
            })}
          </div>
        </div>
        <div style={{ flex:1 }}>
          <p style={{ ...bs, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(201,168,76,0.5)", margin:"0 0 8px" }}>Style</p>
          <div style={{ display:"flex", gap:6 }}>
            {[{id:"dark",icon:"moon",label:"Dark"},{id:"light",icon:"sun",label:"Light"}].map(s => {
              const on = style===s.id;
              return <button key={s.id} onClick={()=>setStyle(s.id)} style={{ flex:1, border:on?`2px solid ${C.gold}`:"1.5px solid rgba(201,168,76,0.2)", borderRadius:10, padding:"8px 4px", background:on?"rgba(201,168,76,0.12)":"rgba(255,255,255,0.04)", cursor:"pointer", ...bs, fontSize:12, fontWeight:on?700:400, color:on?C.gold:"rgba(255,255,255,0.4)", display:"flex", alignItems:"center", justifyContent:"center", gap:6 }}><Icon name={s.icon} size={13} color={on?C.gold:"rgba(255,255,255,0.45)"}/>{s.label}</button>;
            })}
          </div>
        </div>
      </div>

      {/* Card preview */}
      <div style={{ display:"flex", justifyContent:"center", marginBottom:24, padding:"0 20px" }}>
        <div ref={cardRef} style={cardStyle}>
          <div style={{ position:"absolute", top:16, left:16, width:20, height:20, borderTop:`2px solid ${C.gold}`, borderLeft:`2px solid ${C.gold}`, opacity:0.5 }}/>
          <div style={{ position:"absolute", top:16, right:16, width:20, height:20, borderTop:`2px solid ${C.gold}`, borderRight:`2px solid ${C.gold}`, opacity:0.5 }}/>
          <div style={{ position:"absolute", bottom:16, left:16, width:20, height:20, borderBottom:`2px solid ${C.gold}`, borderLeft:`2px solid ${C.gold}`, opacity:0.5 }}/>
          <div style={{ position:"absolute", bottom:16, right:16, width:20, height:20, borderBottom:`2px solid ${C.gold}`, borderRight:`2px solid ${C.gold}`, opacity:0.5 }}/>
          <div style={{ marginBottom:16 }}><LogoMark size={36} color={C.gold}/></div>
          <div style={{ width:40, height:1, background:GRAD.gold, marginBottom:20, borderRadius:1 }}/>
          <p style={{ ...ds, fontSize:isStory?15:14, lineHeight:1.75, color:isDark?"rgba(255,255,255,0.92)":C.green, margin:0, textAlign:"center", fontStyle:"italic", flex:1, display:"flex", alignItems:"center" }}>
            "{customText || "Your words will appear here..."}"
          </p>
          <div style={{ width:40, height:1, background:GRAD.gold, marginTop:20, marginBottom:14, borderRadius:1 }}/>
          <p style={{ ...bs, fontSize:9, letterSpacing:"0.2em", textTransform:"uppercase", color:C.gold, margin:0, opacity:0.7, textAlign:"center" }}>iHart2Manifest</p>
          <p style={{ ...bs, fontSize:8, letterSpacing:"0.12em", color:isDark?"rgba(255,255,255,0.3)":C.textSoft, margin:"3px 0 0", textAlign:"center" }}>YOUR WORDS HAVE POWER. It's Time to Use Them.</p>
        </div>
      </div>

      {/* Edit text */}
      <div style={{ padding:"0 20px", marginBottom:16 }}>
        <p style={{ ...bs, fontSize:10, letterSpacing:"0.18em", textTransform:"uppercase", color:"rgba(201,168,76,0.5)", margin:"0 0 8px" }}>Edit Text</p>
        <textarea value={customText} onChange={e=>setCustomText(e.target.value)} rows={3}
          style={{ width:"100%", boxSizing:"border-box", border:`1.5px solid rgba(201,168,76,0.2)`, borderRadius:14, padding:"12px 14px", ...bs, fontSize:14, background:"rgba(255,255,255,0.06)", color:"rgba(255,255,255,0.85)", outline:"none", resize:"none", lineHeight:1.6 }}/>
      </div>

      {/* Share button */}
      <div style={{ padding:"0 20px 60px" }}>
        <Btn onClick={handleShare} disabled={generating} variant="gold">
          {generating ? "Preparing image..." : copied ? "✓ Copied!" : "✦ Share This"}
        </Btn>
        <p style={{ ...bs, fontSize:11, color:"rgba(255,255,255,0.3)", textAlign:"center", marginTop:10, fontStyle:"italic" }}>
          Shares as a real image to Instagram, Messages & more
        </p>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────
// PREMIUM GATE & UPGRADE MODAL
// ─────────────────────────────────────────────
const PREMIUM_FEATURES = [
  "Unlimited Vision Board images",
  "Full 11-question Dream Builder",
  "Share cards as real images",
  "Unlimited I Am declarations",
  "AI-powered personal affirmations",
  "30-day stats & streak history",
  "All 3 daily reminders",
  "Guided meditation scripts",
];

function UpgradeModal({ onClose, onUpgrade, featureName }) {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:500, background:"rgba(10,20,12,0.92)", backdropFilter:"blur(14px)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"24px" }}>
      <div style={{ background:`linear-gradient(160deg,${C.bg},${C.bgDeep})`, borderRadius:28, padding:"36px 24px 28px", width:"100%", maxWidth:380, boxSizing:"border-box", boxShadow:`0 20px 60px rgba(0,0,0,0.4)`, border:`1px solid rgba(201,168,76,0.2)`, position:"relative" }}>
        {/* Close */}
        <button onClick={onClose} style={{ position:"absolute", top:16, right:16, background:`rgba(44,74,46,0.08)`, border:"none", borderRadius:"50%", width:32, height:32, cursor:"pointer", fontSize:18, color:C.green }}>×</button>

        {/* Logo */}
        <div style={{ textAlign:"center", marginBottom:16 }}>
          <LogoMark size={52} color={C.gold}/>
        </div>

        {/* Heading */}
        <h2 style={{ ...ds, fontSize:24, color:C.green, margin:"0 0 6px", textAlign:"center" }}>Unlock Premium ✦</h2>
        {featureName && <p style={{ ...bs, fontSize:13, color:C.textSoft, textAlign:"center", fontStyle:"italic", margin:"0 0 20px" }}>"{featureName}" is a Premium feature</p>}

        {/* Gold divider */}
        <div style={{ width:48, height:1, background:GRAD.gold, margin:"0 auto 20px", borderRadius:1 }}/>

        {/* Features list */}
        <div style={{ marginBottom:24 }}>
          {PREMIUM_FEATURES.map((f,i) => (
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:10 }}>
              <div style={{ width:20, height:20, borderRadius:"50%", background:GRAD.gold, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ fontSize:10, color:C.green, fontWeight:700 }}>✓</span>
              </div>
              <span style={{ ...bs, fontSize:13, color:C.textMid }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Price */}
        <div style={{ background:`rgba(201,168,76,0.08)`, borderRadius:16, padding:"14px", marginBottom:16, textAlign:"center", border:`1px solid rgba(201,168,76,0.2)` }}>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:`rgba(201,168,76,0.08)`, borderRadius:12, padding:"12px 16px", border:`1px solid rgba(201,168,76,0.15)` }}>
              <div>
                <p style={{ ...bs, fontSize:12, color:C.textSoft, margin:"0 0 2px", textTransform:"uppercase", letterSpacing:"0.1em" }}>Monthly</p>
                <p style={{ ...ds, fontSize:22, color:C.green, margin:0, fontWeight:700 }}>$9.99<span style={{ fontSize:13, fontWeight:400 }}>/mo</span></p>
              </div>
              <span style={{ ...bs, fontSize:11, color:C.textSoft, fontStyle:"italic" }}>Billed monthly</span>
            </div>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", background:`linear-gradient(135deg,rgba(201,168,76,0.15),rgba(201,168,76,0.08))`, borderRadius:12, padding:"12px 16px", border:`2px solid rgba(201,168,76,0.35)`, position:"relative" }}>
              <div style={{ position:"absolute", top:-10, right:12, background:GRAD.gold, borderRadius:20, padding:"3px 10px" }}>
                <span style={{ ...bs, fontSize:10, color:C.green, fontWeight:700, letterSpacing:"0.1em" }}>BEST VALUE</span>
              </div>
              <div>
                <p style={{ ...bs, fontSize:12, color:C.textSoft, margin:"0 0 2px", textTransform:"uppercase", letterSpacing:"0.1em" }}>Annual</p>
                <p style={{ ...ds, fontSize:22, color:C.gold, margin:0, fontWeight:700 }}>$79.99<span style={{ fontSize:13, fontWeight:400 }}>/year</span></p>
              </div>
              <span style={{ ...bs, fontSize:11, color:C.gold, fontWeight:700 }}>Save 33%</span>
            </div>
          </div>
        </div>

        <Btn variant="gold" onClick={()=>{ onUpgrade(); onClose(); }}>
          ✦ Upgrade to Premium
        </Btn>
        <p style={{ ...bs, fontSize:11, color:C.textSoft, textAlign:"center", marginTop:10, fontStyle:"italic" }}>Cancel anytime · Secure payment</p>
      </div>
    </div>
  );
}

// Wrapper that gates content behind premium
function PremiumGate({ isPremium, onUpgrade, featureName, children, fallback }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  if (isPremium) return children;
  return (
    <>
      {showUpgrade && <UpgradeModal featureName={featureName} onClose={()=>setShowUpgrade(false)} onUpgrade={onUpgrade}/>}
      {fallback ? (
        <div onClick={()=>setShowUpgrade(true)}>{fallback}</div>
      ) : (
        <div onClick={()=>setShowUpgrade(true)} style={{ position:"relative", cursor:"pointer" }}>
          <div style={{ opacity:0.4, pointerEvents:"none" }}>{children}</div>
          <div style={{ position:"absolute", inset:0, display:"flex", alignItems:"center", justifyContent:"center", background:"rgba(253,246,233,0.85)", borderRadius:16, backdropFilter:"blur(4px)" }}>
            <div style={{ textAlign:"center", padding:"16px" }}>
              <LogoMark size={28} color={C.gold}/>
              <p style={{ ...bs, fontSize:12, color:C.gold, fontWeight:700, margin:"8px 0 2px", letterSpacing:"0.1em", textTransform:"uppercase" }}>Premium Feature</p>
              <p style={{ ...bs, fontSize:11, color:C.textSoft, margin:0, fontStyle:"italic" }}>Tap to unlock</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Premium locked button
function LockedBtn({ label, onUpgrade, featureName }) {
  const [showUpgrade, setShowUpgrade] = useState(false);
  return (
    <>
      {showUpgrade && <UpgradeModal featureName={featureName} onClose={()=>setShowUpgrade(false)} onUpgrade={onUpgrade}/>}
      <button onClick={()=>setShowUpgrade(true)} style={{ width:"100%", border:`1.5px solid rgba(201,168,76,0.3)`, borderRadius:50, padding:"15px 24px", ...bs, fontSize:16, fontWeight:600, letterSpacing:"0.06em", cursor:"pointer", background:`rgba(201,168,76,0.06)`, color:C.gold, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
        <LogoMark size={16} color={C.gold}/> {label} <span style={{ fontSize:11, opacity:0.6 }}>— Premium</span>
      </button>
    </>
  );
}

// ─────────────────────────────────────────────
// TRIAL SYSTEM
// ─────────────────────────────────────────────
function TrialBanner({ daysLeft, onUpgrade }) {
  if (daysLeft > 5 || daysLeft <= 0) return null;
  const urgent = daysLeft <= 1;
  return (
    <div onClick={onUpgrade} style={{
      background: urgent ? `linear-gradient(135deg,#c87878,#e09090)` : GRAD.gold,
      padding:"10px 20px", cursor:"pointer",
      display:"flex", justifyContent:"space-between", alignItems:"center",
    }}>
      <p style={{ ...bs, fontSize:12, color: urgent?"white":C.green, margin:0, fontWeight:700 }}>
        {urgent ? "Trial ends today — upgrade now" : <span style={{ display:"inline-flex", alignItems:"center", gap:7 }}><Icon name="crown" size={13} color={C.green}/>{`${daysLeft} days left in your free trial`}</span>}
      </p>
      <span style={{ ...bs, fontSize:11, color: urgent?"white":C.green, fontWeight:700, textDecoration:"underline" }}>Upgrade ✦</span>
    </div>
  );
}

function TrialEndScreen({ onUpgrade, onContinueFree }) {
  return (
    <div style={{ position:"fixed", inset:0, zIndex:500, background:`rgba(10,20,12,0.95)`, backdropFilter:"blur(14px)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", padding:"28px" }}>
      <div style={{ background:`linear-gradient(160deg,${C.bg},${C.bgDeep})`, borderRadius:28, padding:"36px 24px 28px", width:"100%", maxWidth:380, boxSizing:"border-box", boxShadow:`0 20px 60px rgba(0,0,0,0.4)`, border:`1px solid rgba(201,168,76,0.2)` }}>
        <div style={{ textAlign:"center", marginBottom:20 }}>
          <LogoMark size={52} color={C.gold}/>
          <h2 style={{ ...ds, fontSize:24, color:C.green, margin:"14px 0 6px" }}>Your Free Trial Has Ended</h2>
          <p style={{ ...bs, fontSize:14, color:C.textSoft, margin:0, fontStyle:"italic", lineHeight:1.6 }}>You've experienced everything iHart2Manifest has to offer. Ready to continue your journey?</p>
        </div>

        <div style={{ width:40, height:1, background:GRAD.gold, margin:"0 auto 20px", borderRadius:1 }}/>

        {/* Features reminder */}
        <div style={{ marginBottom:20 }}>
          {["Unlimited Vision Board","Dream Builder","Share as Image","AI Affirmations","All 3 Reminders"].map((f,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
              <div style={{ width:18, height:18, borderRadius:"50%", background:GRAD.gold, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                <span style={{ fontSize:9, color:C.green, fontWeight:700 }}>✓</span>
              </div>
              <span style={{ ...bs, fontSize:13, color:C.textMid }}>{f}</span>
            </div>
          ))}
        </div>

        {/* Pricing */}
        <div style={{ background:`rgba(201,168,76,0.08)`, borderRadius:16, padding:"14px", marginBottom:16, border:`1px solid rgba(201,168,76,0.2)`, textAlign:"center" }}>
          <p style={{ ...ds, fontSize:26, color:C.gold, margin:"0 0 2px", fontWeight:700 }}>$9.99<span style={{ fontSize:13, fontWeight:400 }}>/month</span></p>
          <p style={{ ...bs, fontSize:12, color:C.textSoft, margin:0, fontStyle:"italic" }}>or $79.99/year — save 33%</p>
        </div>

        <Btn variant="gold" onClick={onUpgrade} style={{ marginBottom:10 }}><span style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:9 }}><Icon name="crown" size={16}/>Continue Premium</span></Btn>
        <button onClick={onContinueFree} style={{ width:"100%", border:"none", background:"transparent", color:C.textSoft, cursor:"pointer", ...bs, fontSize:13, padding:"10px", textDecoration:"underline" }}>
          Continue with free version
        </button>
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────
// ONBOARDING TOOLTIPS
// ─────────────────────────────────────────────
const TOOLTIPS = {
  0: { emoji:"sun", title:"Your Daily Affirmation", body:"Tap New Affirmation for a fresh one anytime. Use the share button to send it to someone who needs it today." },
  1: { emoji:"spark", title:"I Am Declarations", body:"Add declarations from the library or write your own. Tap Declare to go full screen and speak them out loud." },
  2: { emoji:"diamond", title:"Your Vision Board", body:"Add photos of your dream life. Use Slideshow to visualize daily — see it before you receive it." },
  3: { emoji:"leaf", title:"Your Journey", body:"Check in daily, log 3 things you're grateful for, set goals, and track your progress over time." },
};

function TabTooltip({ tabIndex, onDismiss }) {
  const tip = TOOLTIPS[tabIndex];

  // Auto dismiss after 6 seconds
  useEffect(() => {
    const timer = setTimeout(() => onDismiss(), 6000);
    return () => clearTimeout(timer);
  }, []);

  if (!tip) return null;
  return (
    <div style={{
      position:"fixed", bottom:80, left:16, right:16, zIndex:200,
      animation:"tooltipIn .4s cubic-bezier(0.34,1.56,0.64,1)",
    }}>
      <div style={{
        background:`linear-gradient(135deg, rgba(44,74,46,0.97), rgba(28,46,30,0.99))`,
        borderRadius:20, padding:"18px 20px",
        border:`1px solid rgba(201,168,76,0.3)`,
        boxShadow:`0 8px 32px rgba(0,0,0,0.3)`,
        display:"flex", alignItems:"flex-start", gap:14,
      }}>
        {/* Icon */}
        <div style={{ width:40, height:40, borderRadius:12, background:GRAD.gold, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <Icon name={tip.emoji} size={19} color={C.green}/>
        </div>
        {/* Text */}
        <div style={{ flex:1 }}>
          <p style={{ ...ds, fontSize:15, color:C.gold, margin:"0 0 4px", fontWeight:600 }}>{tip.title}</p>
          <p style={{ ...bs, fontSize:13, color:"rgba(255,255,255,0.75)", margin:0, lineHeight:1.55 }}>{tip.body}</p>
        </div>
        {/* Dismiss */}
        <button onClick={onDismiss} style={{ background:"rgba(255,255,255,0.1)", border:"none", borderRadius:"50%", width:28, height:28, cursor:"pointer", color:"rgba(255,255,255,0.6)", fontSize:14, flexShrink:0, display:"flex", alignItems:"center", justifyContent:"center" }}>×</button>
      </div>
      {/* Arrow aligned to specific tab */}
      <div style={{ position:"relative", height:14, marginTop:2 }}>
        <div style={{
          position:"absolute",
          left:`calc(${tabIndex * 25}% + 12.5% - 8px)`,
          width:0, height:0,
          borderLeft:"8px solid transparent",
          borderRight:"8px solid transparent",
          borderTop:`8px solid rgba(44,74,46,0.97)`
        }}/>
      </div>
      <style>{`@keyframes tooltipIn{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}`}</style>
    </div>
  );
}

// ─────────────────────────────────────────────
// ── SPLASH SCREEN ──
function SplashScreen({ onDone }) {
  const [phase, setPhase] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setPhase(1), 450),
      setTimeout(() => setPhase(2), 1250),
      setTimeout(() => setPhase(3), 2000),
      setTimeout(() => setPhase(4), 3900),
      setTimeout(() => onDone(), 4550),
    ];
    return () => timers.forEach(clearTimeout);
  }, []);

  return (
    <div style={{
      position:"fixed", inset:0, zIndex:999,
      background: GRAD.bg,
      display:"flex", flexDirection:"column",
      alignItems:"center", justifyContent:"center",
      opacity: phase===4 ? 0 : 1,
      transition: phase===4 ? "opacity 0.6s ease" : "none",
    }}>
      {/* Soft gold halo behind the crest */}
      <div style={{
        position:"absolute", width:440, height:440, borderRadius:"50%",
        background:"radial-gradient(circle, rgba(201,168,76,0.28) 0%, rgba(201,168,76,0.10) 45%, transparent 72%)",
        opacity: phase>=1 ? 1 : 0, transition:"opacity 1.6s ease",
        animation: phase>=3 ? "glowPulse 3s ease-in-out infinite" : "none",
      }}/>

      {/* Crest + wordmark + tagline — single lockup */}
      <div style={{
        position:"relative", width:"74%", maxWidth:300,
        transform: phase>=1 ? "translateY(0) scale(1)" : "translateY(40px) scale(0.55)",
        opacity: phase>=1 ? 1 : 0,
        transition:"all 1.1s cubic-bezier(0.34,1.56,0.64,1)",
      }}>
        <img src="/ihart-crest.png" alt="iHart2Manifest — Your Words Have Power. It's Time to Use Them."
          style={{ width:"100%", height:"auto", display:"block" }}/>
        {/* one-time light sweep across the gold, masked to the artwork's own silhouette */}
        <div style={{
          position:"absolute", inset:0,
          WebkitMaskImage:"url(/ihart-crest.png)", maskImage:"url(/ihart-crest.png)",
          WebkitMaskSize:"contain", maskSize:"contain",
          WebkitMaskRepeat:"no-repeat", maskRepeat:"no-repeat",
          WebkitMaskPosition:"center", maskPosition:"center",
          background:"linear-gradient(115deg, transparent 38%, rgba(255,255,255,0.9) 49%, rgba(255,240,190,0.95) 53%, transparent 64%)",
          backgroundSize:"280% 280%",
          backgroundPosition: phase>=2 ? "115% 50%" : "-45% 50%",
          transition: phase>=2 ? "background-position 1.2s cubic-bezier(0.4,0,0.2,1)" : "none",
          pointerEvents:"none",
        }}/>
      </div>

      {/* Gold line */}
      <div style={{
        width: phase>=3 ? 80 : 0, height:1,
        background: GRAD.gold, marginTop:26, borderRadius:1,
        transition:"width 1.4s ease",
      }}/>
    </div>
  );
}

// ── TAB BAR ──
function TabBar({ active, onChange }) {
  const tabs = [
    {label:"Affirm", icon:"sun"},
    {label:"I Am",   icon:"spark"},
    {label:"Vision", icon:"diamond"},
    {label:"Journey",icon:"leaf"},
  ];

  const handleTabChange = (i) => {
    buzz();
    window.scrollTo({ top:0, behavior:"smooth" });
    onChange(i);
  };

  return (
    <div style={{
      position:"fixed", bottom:0, left:0, right:0, maxWidth:430, margin:"0 auto",
      background:"rgba(253,246,233,0.97)", backdropFilter:"blur(16px)",
      borderTop:`1px solid rgba(201,168,76,0.2)`,
      display:"flex", zIndex:100,
      paddingBottom:"env(safe-area-inset-bottom,10px)",
      boxShadow:"0 -4px 20px rgba(28,46,30,0.08)",
    }}>
      {tabs.map((t,i) => {
        const on = active===i;
        return (
          <button key={i} onClick={()=>handleTabChange(i)} style={{
            flex:1, padding:"12px 4px 8px", border:"none",
            background:"transparent", cursor:"pointer",
            display:"flex", flexDirection:"column", alignItems:"center", gap:3,
            position:"relative",
          }}>
            {on && (
              <div style={{
                position:"absolute", top:0, left:"25%", right:"25%", height:2,
                background:GRAD.gold, borderRadius:"0 0 3px 3px",
              }}/>
            )}
            <span style={{ position:"relative", display:"flex", alignItems:"center", justifyContent:"center", width:30, height:24 }}>
              {on && <span style={{ position:"absolute", width:34, height:34, borderRadius:"50%", background:"radial-gradient(circle, rgba(201,168,76,0.28) 0%, rgba(201,168,76,0) 70%)" }}/>}
              <Icon name={t.icon} size={on?21:19} color={on?C.goldDeep:"rgba(44,74,46,0.42)"} style={{ position:"relative", transition:"all .2s" }}/>
            </span>
            <span style={{ ...bs, fontSize:9, letterSpacing:"0.12em", fontWeight:700, color:on?C.goldDeep:"rgba(44,74,46,0.48)", textTransform:"uppercase", transition:"color .2s" }}>{t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// ── ROOT ──
export default function App() {
  const [profile, setProfile] = useLocalStorage("iHM_profile", null);
  const [tab, setTab] = useState(0);
  const [splashDone, setSplashDone] = useState(false);
  const [ahaShown, setAhaShown] = useLocalStorage("iHM_ahaShown", false);
  const [isPremium, setIsPremium] = useLocalStorage("iHM_premium", false);
  const [devMode, setDevMode] = useLocalStorage("iHM_devMode", false);
  const [trialStart, setTrialStart] = useLocalStorage("iHM_trialStart", null);
  const [trialExpired, setTrialExpired] = useLocalStorage("iHM_trialExpired", false);
  const [seenTooltips, setSeenTooltips] = useLocalStorage("iHM_tooltips", []);
  const [showTooltip, setShowTooltip] = useState(null);

  // Show tooltip when switching to a new tab for the first time
  useEffect(() => {
    if (tab !== null && !seenTooltips.includes(tab)) {
      const timer = setTimeout(() => setShowTooltip(tab), 600);
      return () => clearTimeout(timer);
    }
  }, [tab]);

  const dismissTooltip = () => {
    setSeenTooltips(s => [...s, showTooltip]);
    setShowTooltip(null);
  };

  // Calculate trial days remaining
  const daysLeft = trialStart ? Math.max(0, TRIAL_DAYS - Math.floor((Date.now() - trialStart) / 86400000)) : TRIAL_DAYS;
  const isTrialActive = trialStart && daysLeft > 0 && !isPremium;
  const isTrialOver = trialStart && daysLeft <= 0 && !isPremium;
  const effectivePremium = isPremium || isTrialActive;

  const handleReset = () => {
    try { ["iHM_profile","iHM_aff","iHM_isAI","iHM_streak","iHM_board","iHM_iam","iHM_checkins","iHM_gratitude","iHM_goals","iHM_ahaShown","iHM_premium","iHM_trialStart","iHM_trialExpired","iHM_tooltips"].forEach(k=>localStorage.removeItem(k)); } catch {}
    setProfile(null); setTab(0); setAhaShown(false); setIsPremium(false); setTrialStart(null); setTrialExpired(false);
  };

  const handleOnboardingComplete = (p) => {
    setProfile(p);
    setAhaShown(false);
    if (!trialStart) setTrialStart(Date.now());
  };

  return (
    <div style={{ maxWidth:430, margin:"0 auto", minHeight:"100vh", background:C.bg, position:"relative" }}>
      <link rel="stylesheet" href={FONTS} />
      <style>{`
        @keyframes fadeInUp{from{opacity:0;transform:translateY(16px)}to{opacity:1;transform:translateY(0)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}
        @keyframes floatIn{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
        @keyframes dotPulse{0%,100%{transform:scale(1);opacity:.45}50%{transform:scale(1.55);opacity:1}}
        @keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}
        @keyframes glowPulse{0%,100%{transform:scale(1);opacity:.75}50%{transform:scale(1.1);opacity:1}}
        @keyframes kenburns{from{transform:scale(1) translateY(0)}to{transform:scale(1.09) translateY(-8px)}}
        button{-webkit-tap-highlight-color:transparent}
        button:active:not(:disabled){transform:scale(.965)}
        input,textarea{-webkit-tap-highlight-color:transparent}
        @media (prefers-reduced-motion: reduce){*{animation:none !important;transition:none !important}}
      `}</style>
      {!splashDone && <SplashScreen onDone={()=>setSplashDone(true)} />}
      {splashDone && (
        <>
          {!profile && <Onboarding onComplete={handleOnboardingComplete} />}
          {profile && !ahaShown && <AhaMoment profile={profile} onEnter={()=>setAhaShown(true)} />}
          {profile && ahaShown && (
            <>
              {/* Trial expired screen */}
              {isTrialOver && <TrialEndScreen onUpgrade={()=>setIsPremium(true)} onContinueFree={()=>{ setTrialStart(1); setTrialExpired(true); }}/>}

              {/* Trial banner */}
              {isTrialActive && <TrialBanner daysLeft={daysLeft} onUpgrade={()=>setIsPremium(true)}/>}

              <div key={tab} style={{ animation:"fadeIn .32s ease" }}>
              {tab===0 && <AffirmationsTab profile={profile} onReset={handleReset} onUpdateProfile={updates=>setProfile(p=>({...p,...updates}))} isPremium={effectivePremium} onUpgrade={()=>setIsPremium(true)} devMode={devMode} onToggleDev={()=>setDevMode(d=>!d)} onTogglePremium={()=>setIsPremium(p=>!p)} daysLeft={daysLeft} isTrialActive={isTrialActive}/>}
              {tab===1 && <IAmTab isPremium={effectivePremium} onUpgrade={()=>setIsPremium(true)} />}
              {tab===2 && <VisionBoardTab profile={profile} isPremium={effectivePremium} onUpgrade={()=>setIsPremium(true)} />}
              {tab===3 && <JourneyTab profile={profile} onUpdateDream={dream=>setProfile(p=>({...p,dream}))} isPremium={effectivePremium} onUpgrade={()=>setIsPremium(true)} />}
              </div>
              <TabBar active={tab} onChange={setTab} />
              {showTooltip !== null && <TabTooltip tabIndex={showTooltip} onDismiss={dismissTooltip}/>}
            </>
          )}
        </>
      )}
    </div>
  );
}
